import { getAuth } from 'firebase/auth';
import { emitBillingBlocked, parseBillingBlockedPayload } from '@modules/billing-access';
import {
  AdCreativeRequestTransport,
  type AdCreativeBinaryTransportResponse,
} from './AdCreativeRequestTransport';
import {
  buildAdCreativeApprovedAudioCandidateListPath,
  buildAdCreativeApprovedAudioCandidatePlaybackPath,
  buildAdCreativeApprovedAudioCandidateReadPath,
  buildAdCreativeApprovedAudioCandidateReviewPath,
  buildAdCreativeApprovedAudioCandidateUploadPath,
  type AdCreativeApprovedAudioCandidateListResponse,
  type AdCreativeApprovedAudioCandidateReadResponse,
  type AdCreativeApprovedAudioCandidateReviewFields,
  type AdCreativeApprovedAudioCandidateReviewResponse,
  type AdCreativeApprovedAudioCandidateUploadFields,
  type AdCreativeApprovedAudioCandidateUploadResponse,
} from './AdCreativeApprovedAudioApiContract';
export type {
  AdCreativeApprovedAudioCandidateListResponse,
  AdCreativeApprovedAudioCandidateReadResponse,
  AdCreativeApprovedAudioCandidateReviewFields,
  AdCreativeApprovedAudioCandidateReviewResponse,
  AdCreativeApprovedAudioCandidateUploadFields,
  AdCreativeApprovedAudioCandidateUploadResponse,
  AdCreativeApprovedAudioCandidateView,
} from './AdCreativeApprovedAudioApiContract';
import {
  buildProviderCampaignPageCall,
  buildProviderSourceBindingsCall,
  buildProviderSourceDetailCall,
  buildProviderSourceBaselineSelectionCall,
  buildProviderSourceLanguageDetectionPreflightCall,
  buildProviderSourceLanguageDetectionStartCall,
  buildProviderSourceTaxonomyPreflightCall,
  buildProviderSourceTaxonomyStartCall,
  buildProviderSourceTaxonomyReadCall,
  buildProviderSourceTaxonomyReviewCall,
  buildProviderSourceLanguageConfirmationCall,
  buildProviderSourceLanguageEvidenceCall,
  buildProviderSourceCandidateListCall,
  buildProviderSourceCandidatePromotionPreflightCall,
  buildProviderSourceReportRowPreflightCall,
  buildProviderSourceListCall,
  buildProviderSourceSyncCancelCall,
  buildProviderSourceSyncCall,
  buildProviderSourceSyncPreflightCall,
  buildProviderSourceSyncProgressCall,
  buildProviderSourceSyncResumeCall,
  buildProviderSourceVariationDraftCall,
  type AdCreativeProviderSourceLanguageDetectionPreflightRequest,
  type AdCreativeProviderSourceLanguageDetectionStartRequest,
  type AdCreativeProviderSourceTaxonomyPreflightRequest,
  type AdCreativeProviderSourceTaxonomyStartRequest,
  type AdCreativeProviderSourceTaxonomyReviewRequest,
  type AdCreativeProviderSourceCandidatePromotionPreflightRequest,
  type AdCreativeProviderSourceReportRowPreflightRequest,
  type AdCreativeProviderSourceSyncPreflightRequest,
  type AdCreativeProviderSourceSyncStartRequest,
} from './AdCreativeProviderSourceApiContract';
export type {
  AdCreativeProviderSourceLanguageDetectionPreflightRequest,
  AdCreativeProviderSourceLanguageDetectionStartRequest,
  AdCreativeProviderSourceTaxonomyPreflightRequest,
  AdCreativeProviderSourceTaxonomyStartRequest,
  AdCreativeProviderSourceTaxonomyReviewRequest,
  AdCreativeProviderSourceCandidatePromotionPreflightRequest,
  AdCreativeProviderSourceReportRowPreflightRequest,
  AdCreativeProviderSourceSyncPreflightRequest,
  AdCreativeProviderSourceSyncStartRequest,
} from './AdCreativeProviderSourceApiContract';
import {
  buildManifestVariationCall,
  type CreateAdCreativeManifestVariationRequest,
  type CreateAdCreativeManifestVariationResponse,
} from './AdCreativeManifestVariationApiContract';
import {
  buildCaptionReviewReadCall,
  buildCulturalVisualListCall,
  buildCulturalVisualReviewCall,
  buildImageReviewReadCall,
  buildLocalizationJobMutationCall,
  buildLocalizationJobReadCall,
  buildLocalizationPreflightCall,
  buildLocalizationStartCall,
  buildManualVideoTranscriptImportCall,
  buildSourceImageInspectionCall,
  buildVideoTranscriptCreateCall,
  buildVideoTranscriptListCall,
  buildVideoTranscriptReadCall,
  buildVideoTranscriptSelectCall,
  buildVideoTextInspectionListCall,
  buildVideoTextInspectionReviewCall,
  buildVideoVoiceRightsApproveCall,
  buildVideoVoiceRightsListCall,
  buildVideoVoiceRightsRevokeCall,
  type AdCreativeLocalizationComponent,
  type AdCreativeCulturalVisualListRequest,
  type AdCreativeCulturalVisualListResponse,
  type AdCreativeCulturalVisualReviewRequest,
  type AdCreativeCulturalVisualReviewResponse,
  type AdCreativeCaptionReviewResponse,
  type AdCreativeImageReviewResponse,
  type AdCreativeManualVideoTranscriptImportRequest,
  type AdCreativeLocalizationJobResponse,
  type AdCreativeLocalizationJobMutationResponse,
  type AdCreativeLocalizationPreflightRequest,
  type AdCreativeLocalizationPreflightResponse,
  type AdCreativeLocalizationStartRequest,
  type AdCreativeLocalizationStartResponse,
  type AdCreativeSourceImageInspectionRequest,
  type AdCreativeSourceImageInspectionResponse,
  type AdCreativeVideoTranscriptCreateRequest,
  type AdCreativeVideoTranscriptListRequest,
  type AdCreativeVideoTranscriptListResponse,
  type AdCreativeVideoTranscriptMutationResponse,
  type AdCreativeVideoTranscriptReadRequest,
  type AdCreativeVideoTranscriptReadResponse,
  type AdCreativeVideoTranscriptSelectRequest,
  type AdCreativeVideoTranscriptStartResponse,
  type AdCreativeVideoTextInspectionListRequest,
  type AdCreativeVideoTextInspectionListResponse,
  type AdCreativeVideoTextInspectionReviewRequest,
  type AdCreativeVideoTextInspectionReviewResponse,
  type AdCreativeVideoVoiceRightsApproveRequest,
  type AdCreativeVideoVoiceRightsListRequest,
  type AdCreativeVideoVoiceRightsListResponse,
  type AdCreativeVideoVoiceRightsMutationResponse,
  type AdCreativeVideoVoiceRightsRevokeRequest,
} from './AdCreativeLocalizationApiContract';
export type {
  AdCreativeCaptionReviewResponse,
  AdCreativeCaptionReviewView,
  AdCreativeCulturalSceneReviewInput,
  AdCreativeCulturalVisualEvidenceView,
  AdCreativeCulturalVisualLibraryView,
  AdCreativeCulturalVisualListRequest,
  AdCreativeCulturalVisualListResponse,
  AdCreativeCulturalVisualReviewRequest,
  AdCreativeCulturalVisualReviewResponse,
  AdCreativeImageReviewCheck,
  AdCreativeImageReviewOperation,
  AdCreativeImageReviewResponse,
  AdCreativeImageReviewSlotView,
  AdCreativeImageReviewTextBox,
  AdCreativeImageReviewTextObservation,
  AdCreativeImageReviewView,
  AdCreativeAiTextLocalizationContext,
  AdCreativeImageLocalizationMode,
  AdCreativeImageLocalizationJobResponse,
  AdCreativeImageLocalizationPreflight,
  AdCreativeImageLocalizationPreflightRequest,
  AdCreativeImageLocalizationPreflightResponse,
  AdCreativeImageLocalizationResult,
  AdCreativeImageLocalizationSlotContext,
  AdCreativeImageLocalizationSlotPreflight,
  AdCreativeImageLocalizationStartRequest,
  AdCreativeImageLocalizationStartResponse,
  AdCreativeLocalizationComponent,
  AdCreativeLocalizationJobResponse,
  AdCreativeLocalizationJobMutationResponse,
  AdCreativeLocalizationLocaleJobResult,
  AdCreativeLocalizationPreflight,
  AdCreativeLocalizationPreflightRequest,
  AdCreativeLocalizationPreflightResponse,
  AdCreativeLocalizationRuntimeStatus,
  AdCreativeLocalizationStartRequest,
  AdCreativeLocalizationStartResponse,
  AdCreativeLocalizationUnsupportedReason,
  AdCreativeMediaLocalizationJobView,
  AdCreativeMediaLocalizationResult,
  AdCreativeMediaLocalizationTaskView,
  AdCreativeTextLocalizationCostEstimate,
  AdCreativeTextLocalizationInputEntry,
  AdCreativeTextLocalizationJobResponse,
  AdCreativeTextLocalizationJobView,
  AdCreativeTextLocalizationPreflight,
  AdCreativeTextLocalizationPreflightRequest,
  AdCreativeTextLocalizationPreflightResponse,
  AdCreativeTextLocalizationStartRequest,
  AdCreativeTextLocalizationStartResponse,
  AdCreativeTextLocalizationTaskView,
  AdCreativeVideoLocalizationJobResponse,
  AdCreativeVideoLocalizationPreflight,
  AdCreativeVideoLocalizationPreflightRequest,
  AdCreativeVideoLocalizationPreflightResponse,
  AdCreativeVideoLocalizationResult,
  AdCreativeVideoLocalizationStartRequest,
  AdCreativeVideoLocalizationStartResponse,
  AdCreativeVideoLocalizationTier,
  AdCreativeVideoLocalizationTierPreflight,
  AdCreativeSourceImageEvidenceView,
  AdCreativeSourceImageInspectionRequest,
  AdCreativeSourceImageInspectionResponse,
  AdCreativeManualVideoTranscriptImportRequest,
  AdCreativeTimedTranscriptCueView,
  AdCreativeTimedTranscriptView,
  AdCreativeVideoTranscriptCreateRequest,
  AdCreativeVideoTranscriptLibraryView,
  AdCreativeVideoTranscriptListRequest,
  AdCreativeVideoTranscriptListResponse,
  AdCreativeVideoTranscriptMutationResponse,
  AdCreativeVideoTranscriptReadRequest,
  AdCreativeVideoTranscriptReadResponse,
  AdCreativeVideoTranscriptSelectRequest,
  AdCreativeVideoTranscriptStartResponse,
  AdCreativeVideoTranscriptTaskStatus,
  AdCreativeVideoTranscriptTaskView,
  AdCreativeVideoTranscriptVersionView,
  AdCreativeVideoTextInspectionEvidenceView,
  AdCreativeVideoTextInspectionLibraryView,
  AdCreativeVideoTextInspectionListRequest,
  AdCreativeVideoTextInspectionListResponse,
  AdCreativeVideoTextInspectionRegionInput,
  AdCreativeVideoTextInspectionReviewRequest,
  AdCreativeVideoTextInspectionReviewResponse,
  AdCreativeVideoTextRegionBounds,
  AdCreativeVoiceRightsBasis,
  AdCreativeVoiceRightsOperation,
  AdCreativeVideoVoiceRightsApproveRequest,
  AdCreativeVideoVoiceRightsLibraryView,
  AdCreativeVideoVoiceRightsListRequest,
  AdCreativeVideoVoiceRightsListResponse,
  AdCreativeVideoVoiceRightsMutationResponse,
  AdCreativeVideoVoiceRightsRevokeRequest,
  AdCreativeVideoVoiceRightsStatus,
  AdCreativeVideoVoiceRightsSummaryView,
} from './AdCreativeLocalizationApiContract';
import type {
  AdCreativeGenerationRun,
  AdCreativeItem,
  AdCreativePerformanceFeedback,
  AdCreativeTemplate,
  AdCreativeType,
  AdCreativeFormat,
  AdNetwork,
  AdCreativeBrief,
  AdCreativePlan,
  AdCreativeValidationRun,
  AdCreativeExportManifest,
  AdCreativeDesignCollection,
  AdCreativeDesignPreset,
  AdCreativeLocalizationManifest,
  AdCreativeLocalizationOperation,
  AdCreativeLocalizationVariant,
  AdCreativeDestinationRef,
  AdCreativeAssetReference,
} from '@segmently/schema-models/ad-creative';
import type {
  BrandStyleKit,
} from '@segmently/schema-models/content-plan';
import type {
  AdCreativePlatformSettings,
} from '@segmently/schema-models/ad-creative';

/** Hard ceiling per request; aborted requests resolve as { success:false, aborted:true }. */
const REQUEST_TIMEOUT_MS = 30_000;
const PROVIDER_SOURCE_PREFLIGHT_TIMEOUT_MS = 5 * 60_000;

/** FR7: curated creative framework served with the catalog (additive). */
export interface AdCreativeFrameworkOption {
  id: string;
  kind: 'ugc_video' | 'static';
  name: string;
  formatFamilies: string[];
  funnelStage: string[];
  gist: string;
  structure: string[];
  skeleton?: string;
  lengthSec?: number;
  energy?: string;
  insightAffinities: Record<string, number>;
  designNotes?: string;
}

/** FR8: one reviewable draft creative brief (angle × format cell). */
export interface AdCreativeBriefDraft {
  id: string;
  kind: 'creative_brief';
  templateId: string;
  templateName?: string;
  formatFamily?: string;
  mediaKind: 'video' | 'static';
  angleId: string;
  angleLabel?: string;
  status: 'draft' | 'generated';
  generatedTaskId?: string;
  edited?: boolean;
  warnings?: string[];
  data: {
    concept: string;
    hookIdea?: string;
    proofStrategy?: string;
    beats: Array<{ label: string; text: string; sourcePath?: string }>;
    frameworkId?: string;
    frameworkName?: string;
    insightRefs: string[];
    audienceName?: string;
    cta?: string;
  };
}

export interface AdCreativeCatalogResponse {
  success: boolean;
  data?: {
    network: AdNetwork;
    formats: AdCreativeFormat[];
    templates: AdCreativeTemplate[];
    frameworks?: AdCreativeFrameworkOption[];
    frameworkFamilyNotes?: Record<string, string>;
  };
  error?: string;
}

export interface AdCreativeDestinationCompatibilityView {
  status: 'not_applicable' | 'compatible' | 'warning' | 'blocked';
  code: string;
  targetLocale: string;
  message: string;
  approvalRequired: boolean;
  approvalUsed: boolean;
}

export interface AdCreativeDestinationOptionView {
  kind: 'funnel' | 'article';
  id: string;
  label: string;
  destination: AdCreativeDestinationRef;
  publishedUrl?: string;
  defaultLanguage?: string;
  languages: string[];
  compatible: boolean;
  resolvedLocale?: string;
  reason: string;
}

export interface AdCreativeDestinationCatalogView {
  creativeItemId: string;
  targetLocale: string;
  selectedDestination?: AdCreativeDestinationRef;
  selectedCompatibility: AdCreativeDestinationCompatibilityView;
  options: AdCreativeDestinationOptionView[];
}

export interface AdCreativeLocalizationImageUploadResponse {
  success: boolean;
  data?: {
    asset: AdCreativeAssetReference;
    evidenceId: string;
    byteSize: number;
    mediaKind: 'image' | 'poster';
  };
  error?: string;
}

export interface AdCreativeLocalizationVideoUploadResponse {
  success: boolean;
  data?: {
    asset: AdCreativeAssetReference & {
      mimeType: 'video/mp4' | 'video/webm';
      width: number;
      height: number;
      durationMs: number;
      sha256: string;
    };
    evidenceId: string;
    byteSize: number;
    objectGeneration: string;
    mediaKind: 'video';
  };
  error?: string;
  code?: string;
}

export interface AdCreativeLocalizationVideoUploadIntentResponse {
  success: boolean;
  data?: {
    intentId: string;
    upload: {
      method: 'PUT';
      url: string;
      headers: Record<string, string>;
    };
    expiresAt: string;
    byteSize: number;
    mimeType: 'video/mp4' | 'video/webm';
  };
  error?: string;
  code?: string;
}

export type AdCreativeStagedVideoUploadProgress =
  | { phase: 'preparing'; percent: 0 }
  | { phase: 'uploading'; percent: number }
  | { phase: 'finalizing'; percent: 100 };

export interface GenerateAdCreativeRequest {
  projectId: string;
  briefId?: string;
  planId?: string;
  campaignId?: string;
  networkId?: string;
  creativeTypes?: AdCreativeType[];
  templateIds?: string[];
  objective?: string;
  audience?: Record<string, unknown>;
  offer?: Record<string, unknown>;
  proof?: Record<string, unknown>;
  designSystem?: Record<string, unknown>;
  designCollectionId?: string;
  selectedDesignPresetIds?: string[];
  designPresetByCreativeType?: Record<string, string>;
  brandStyleKit?: BrandStyleKit;
  feedback?: AdCreativePerformanceFeedback;
  destination?: Record<string, unknown>;
  sourceRefs?: Record<string, unknown>;
  sourcePostSignature?: Record<string, unknown>;
  plan?: AdCreativePlan;
  variantCount?: number;
  generateAssets?: boolean;
  slotLimitPerCreative?: number;
  imageModel?: string;
  imageSize?: string;
  /** FR4: explicit model/effort/budget overrides for the text agents. */
  generation?: { textModel?: string; reasoningEffort?: 'low' | 'medium' | 'high'; maxOutputTokens?: number };
}

export interface CreateAdCreativeBriefRequest {
  projectId: string;
  campaignId?: string;
  title?: string;
  networkId?: string;
  objective?: string;
  kpi?: string;
  audience?: Record<string, unknown>;
  task?: Record<string, unknown>;
  destination?: Record<string, unknown>;
  placementFamilies?: string[];
  productContext?: Record<string, unknown>;
  sourceRefs?: Record<string, unknown>;
  sourcePostSignature?: Record<string, unknown>;
  selectedTemplateIds?: string[];
  designCollectionId?: string;
  selectedDesignPresetIds?: string[];
  styleAnchorOverride?: Record<string, unknown>;
  variantBudget?: number;
}

/**
 * Review-gated Creative Decisions -> Ad Creative refresh lineage.
 *
 * This package intentionally contains identities and a pre-registered test
 * protocol only. Mutable delivery/performance values and raw PII do not belong
 * in this boundary; the server canonicalizes and fingerprints it before the
 * new Segmently brief is persisted.
 */
export interface CreativeDecisionRefreshHandoffPackage {
  schemaVersion: 1;
  sourceKind: 'creative_decision';
  /** Inclusive UTC date range used to reproduce the exact server projection. */
  sourceFrom: string;
  sourceTo: string;
  deploymentKey: string;
  projectionId: string;
  projectionVersion: string;
  scopeRef: string;
  provider: 'meta' | 'google_ads' | 'tiktok_ads';
  providerAccountId: string;
  providerResourceName: string;
  capabilitySnapshotId: string;
  sourceRevisionRefs: string[];
  artifactSnapshotKeys: string[];
  decision: 'refresh_due';
  primaryAction: 'create_refresh_experiment';
  decisionMetricKeys: string[];
  evidenceRefs: string[];
  variation: 'test_hooks_and_visuals' | 'test_reels_adaptation' | 'test_alternative_angles';
  fixedFactors: string[];
  factorsToVary: string[];
  targetEstimand: 'intention_to_treat_contribution_per_assigned_user';
  primaryMetric: 'profit_per_assigned_user';
  surrogateStatus: 'diagnostic_only';
  guardrails: string[];
  minimumDetectableEffectPercent: number;
  minimumDurationDays: number;
  stoppingPolicy: 'fixed_horizon_no_peeking' | 'registered_sequential';
  sourceAsOf: string;
}

export interface GenerateAdCreativeBriefTopicsRequest {
  projectId: string;
  campaignId?: string;
  networkId?: string;
  model?: string;
  objective?: string;
  kpi?: string;
  count?: number;
  audience?: Record<string, unknown>;
  task?: Record<string, unknown>;
  destination?: Record<string, unknown>;
  placementFamilies?: string[];
  productContext?: Record<string, unknown>;
  sourceRefs?: Record<string, unknown>;
  selectedTemplateIds?: string[];
  designCollectionId?: string;
  selectedDesignPresetIds?: string[];
  userInstruction?: string;
}

export interface SaveAdCreativeDesignCollectionRequest {
  projectId: string;
  networkId: string;
  collectionId?: string;
  name?: string;
  description?: string;
  isCurrent?: boolean;
  status?: AdCreativeDesignCollection['status'];
  styleAnchor?: Record<string, unknown>;
  referenceStrategy?: Record<string, unknown>;
  presets?: Array<Partial<AdCreativeDesignPreset> & {
    creativeType: AdCreativeDesignPreset['creativeType'];
  }>;
}

export interface GenerateAdCreativeDesignCollectionRequest {
  projectId: string;
  networkId: string;
  model?: string;
  collectionId?: string;
  baseCollectionId?: string;
  name?: string;
  description?: string;
  sourceBrief?: string;
  referenceAssets?: Array<Record<string, unknown>>;
  selectedCreativeTypes?: AdCreativeDesignPreset['creativeType'][];
  setCurrent?: boolean;
}

export interface AdCreativeCampaign {
  id: string;
  projectId: string;
  title: string;
  status: string;
  objectiveId?: string;
  primaryKpi?: string;
  networkId: string;
  selectedPublisherPlatforms: string[];
  selectedPlacementFamilies: string[];
  campaignBriefIds?: string[];
  launchPlanIds?: string[];
  learningSnapshotIds?: string[];
  improvementRunIds?: string[];
  latestLaunchPlanId?: string;
  latestLearningSnapshotId?: string;
  latestImprovementRunId?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface CreateAdCreativeCampaignRequest {
  projectId: string;
  title?: string;
  objectiveId?: string;
  primaryKpi?: string;
  networkId?: string;
  selectedPublisherPlatforms?: string[];
  selectedPlacementFamilies?: string[];
  destinationRefs?: Array<Record<string, unknown>>;
  dateRange?: Record<string, unknown>;
}

export interface ListCampaignsResponse {
  success: boolean;
  data?: { campaigns: AdCreativeCampaign[] };
  error?: string;
}

export interface CampaignResponse {
  success: boolean;
  data?: { campaign: AdCreativeCampaign };
  error?: string;
}

export interface AdCreativeTaskDocument {
  id: string;
  taskType: string;
  status: string;
  phase?: string;
  progress?: number;
  projectId?: string;
  creativeItemId?: string;
  slotKey?: string;
  sourceRevision?: string;
  sourceComponentHash?: string;
  provider?: string;
  model?: string;
  result?: Record<string, unknown>;
  error?: { code?: string; message?: string };
  cancelRequested?: boolean;
  currentPhaseKey?: string;
  phases?: Record<string, unknown>;
  artifacts?: Record<string, unknown>;
}

export interface AdCreativeResearchRun {
  id: string;
  status: string;
  researchBrief?: Record<string, unknown>;
  researchAnalysis?: Record<string, unknown>;
  sourceRefs?: Record<string, unknown>;
}

export interface CreateResearchResponse {
  success: boolean;
  data?: { task: AdCreativeTaskDocument; researchRun?: AdCreativeResearchRun };
  error?: string;
}

export interface GenerateAnglesResponse {
  success: boolean;
  data?: { task: AdCreativeTaskDocument; angles?: Array<Record<string, unknown>> };
  error?: string;
}

export interface GenerateBriefTopicsResponse {
  success: boolean;
  data?: { task: AdCreativeTaskDocument; briefs?: AdCreativeBrief[] };
  error?: string;
}

export interface ApplyBriefTopicsResponse {
  success: boolean;
  data?: {
    appliedCount: number;
    skippedCount: number;
    rejectedCount: number;
    appliedBriefIds: string[];
  };
  error?: string;
}

export interface LaunchPlanResponse {
  success: boolean;
  data?: { task: AdCreativeTaskDocument; launchPlan?: Record<string, unknown> };
  error?: string;
}

export interface LaunchExportResponse {
  success: boolean;
  data?: { task: AdCreativeTaskDocument; manifest?: Record<string, unknown> };
  error?: string;
}

export interface MetricsImportResponse {
  success: boolean;
  data?: {
    task: AdCreativeTaskDocument;
    dataState: 'processing' | 'no_data';
    learningSnapshot?: Record<string, unknown>;
  };
  error?: string;
}

export interface ImproveCampaignResponse {
  success: boolean;
  data?: { task: AdCreativeTaskDocument; improvementRun?: Record<string, unknown> };
  error?: string;
}

export interface GetAdCreativeTaskResponse {
  success: boolean;
  data?: { task: AdCreativeTaskDocument };
  error?: string;
}

export interface BriefResponse {
  success: boolean;
  data?: { brief: AdCreativeBrief };
  error?: string;
}

export interface ListBriefsResponse {
  success: boolean;
  data?: { briefs: AdCreativeBrief[] };
  error?: string;
}

export interface PlanResponse {
  success: boolean;
  data?: { plan: AdCreativePlan };
  error?: string;
}

export interface GenerateAdCreativeResponse {
  success: boolean;
  data?: {
    task?: AdCreativeTaskDocument;
    run: AdCreativeGenerationRun;
    items: AdCreativeItem[];
    generatedImageCount: number;
    skippedImageCount: number;
  };
  error?: string;
}

export interface GenerateAdCreativeTaskResponse {
  success: boolean;
  data?: {
    task: AdCreativeTaskDocument;
    run?: AdCreativeGenerationRun;
    items?: AdCreativeItem[];
    generatedImageCount?: number;
    skippedImageCount?: number;
  };
  error?: string;
}

export interface ListAdCreativeItemsResponse {
  success: boolean;
  data?: {
    items: AdCreativeItem[];
  };
  error?: string;
}

export interface ListAdCreativeNetworksResponse {
  success: boolean;
  data?: {
    networks: AdNetwork[];
  };
  error?: string;
}

export interface ListAdCreativeDesignCollectionsResponse {
  success: boolean;
  data?: {
    collections: AdCreativeDesignCollection[];
  };
  error?: string;
}

export interface AdCreativeDesignCollectionResponse {
  success: boolean;
  data?: {
    collection: AdCreativeDesignCollection;
  };
  error?: string;
}

export interface GenerateAdCreativeDesignCollectionResponse {
  success: boolean;
  data?: {
    task: AdCreativeTaskDocument;
    collection?: AdCreativeDesignCollection;
  };
  error?: string;
}

export interface UpsertAdCreativeNetworkRequest {
  id: string;
  displayName: string;
  provider?: string;
  icon?: string;
  status?: AdNetwork['status'];
  policySummary?: string;
  defaultFormatIds?: string[];
  defaultPlacementIds?: string[];
  isActive?: boolean;
}

export interface MetaAdsConnectionStatus {
  connected: boolean;
  providerConfigured?: boolean;
  needsReconnect?: boolean;
  metaUserId?: string;
  displayName?: string;
  scopes?: string[];
  accessibleAdAccounts?: MetaAdsAdAccountSummary[];
}

export interface MetaAdsAdAccountSummary {
  id: string;
  accountId?: string;
  name?: string;
  currency?: string;
  timezoneName?: string;
}

export interface MetaAdsPageSummary {
  id: string;
  name?: string;
  category?: string;
  tasks?: string[];
}

export interface MetaAdsCampaignSummary {
  id: string;
  name?: string;
  objective?: string;
  configuredStatus?: string;
  effectiveStatus?: string;
}

export interface MetaAdsCampaignPageResponse {
  campaigns?: MetaAdsCampaignSummary[];
  hasMore?: boolean;
  nextCursor?: string;
  success?: boolean;
  error?: string;
}

export interface MetaAdsAdSetSummary {
  id: string;
  campaignId?: string;
  name?: string;
  configuredStatus?: string;
  effectiveStatus?: string;
}

export type AdCreativeProviderSourceLanguageOrigin =
  | 'provider_declared'
  | 'user_confirmed'
  | 'ai_detected'
  | 'heuristic_detected'
  | 'unknown';

export interface AdCreativeProviderSourceLanguageProjection {
  locale?: string;
  origin: AdCreativeProviderSourceLanguageOrigin;
  confidence?: number;
  mixedLanguage: boolean;
  evidenceRef: string;
}

export interface AdCreativeProviderSourceHead {
  id: string;
  projectId: string;
  provider: 'meta';
  providerAccountId: string;
  providerCreativeId: string;
  providerIdentityKey: string;
  currentSnapshotId: string;
  creativeName?: string;
  primaryText?: string;
  headline?: string;
  description?: string;
  ctaType?: string;
  destinationUrl?: string;
  presentationState: 'exact_static' | 'candidate_set' | 'provider_generated_unknown';
  capabilityState: 'full_media' | 'partial_media' | 'metadata_only' | 'dynamic_manifest';
  capabilityReasons: string[];
  mediaSummary: {
    primaryMediaRef?: string;
    mediaCount: number;
    videoCount: number;
    carouselCardCount: number;
    mirrorState: 'mirrored' | 'partial' | 'provider_reference_only';
  };
  variantSummary: {
    primaryTextCount: number;
    headlineCount: number;
    descriptionCount: number;
    mediaCandidateCount: number;
  };
  sourceLanguage: AdCreativeProviderSourceLanguageProjection;
  deploymentCount: number;
  campaignCount: number;
  countsScope: 'latest_bounded_sync';
  taxonomyState: 'not_analyzed' | 'suggested' | 'reviewed' | 'rejected' | 'stale';
  currentTaxonomySnapshotId?: string;
  taxonomySourceSnapshotId?: string;
  firstSyncedAt: string;
  lastSyncedAt: string;
  lastSuccessfulSyncRunId: string;
}

export interface AdCreativeProviderSourceTaxonomyFields {
  topic: { label: string; summary: string };
  angle: { label: string; thesis: string };
  concept: { label: string; description: string };
  hook: { label: string; pattern: string };
  proof: { strategy: string; evidenceEntryKeys: string[]; caution: string };
  format: { family: string; rationale: string };
  confidence: number;
  warnings: string[];
}

export interface AdCreativeProviderSourceTaxonomySnapshot extends AdCreativeProviderSourceTaxonomyFields {
  id: string;
  schemaVersion: 1;
  immutable: true;
  projectId: string;
  sourceId: string;
  sourceSnapshotId: string;
  sourceSnapshotContentHash: string;
  state: 'suggested' | 'reviewed' | 'rejected';
  taxonomyVersion: string;
  evidenceEntries: Array<{ entryKey: string; field: string }>;
  contentHash: string;
  supersedesTaxonomySnapshotId?: string;
  review?: {
    decision: 'approve' | 'reject';
    reviewedBy: string;
    reviewedAt: string;
    note?: string;
  };
  createdAt: string;
  createdBy: string;
}

export interface AdCreativeProviderSourceTaxonomyPreflight {
  preflightToken: string;
  issuedAt: string;
  expiresAt: string;
  sourceCount: number;
  sources: Array<{ sourceId: string; snapshotId: string; evidenceEntryCount: number }>;
  inputHash: string;
  model: string;
  provider: string;
  taxonomyVersion: string;
  costEstimate: {
    estimatedInputTokens: number;
    estimatedOutputTokens: number;
    estimatedCostUsd: number;
  };
}

export interface AdCreativeProviderSourceTaxonomyCurrent {
  sourceId: string;
  currentSourceSnapshotId: string;
  state: AdCreativeProviderSourceHead['taxonomyState'];
  taxonomy?: AdCreativeProviderSourceTaxonomySnapshot;
}

export interface AdCreativeProviderSourceCandidate {
  id: string;
  projectId: string;
  provider: 'meta';
  providerAccountId: string;
  providerAdId: string;
  providerCreativeId?: string;
  sourceId?: string;
  campaignId?: string;
  campaignName?: string;
  adSetId?: string;
  adSetName?: string;
  adName?: string;
  effectiveStatus?: string;
  boundedDestinationUrls: string[];
  funnelAssociation: {
    state: 'linked_in_window' | 'known_segmently_destination' | 'mixed' | 'not_linked' | 'unknown';
    qualifiesForSegmentlyMedia: boolean;
    assignmentModelId: string;
    assignmentView: 'single_touch';
    window: { from: string; to: string; semantics: 'from_inclusive_to_exclusive' };
    linkageEvidenceCount: number;
    destinationEvidenceCount: number;
  };
  discoveryState: 'candidate' | 'promoted' | 'unavailable';
  latestObservedAt: string;
  lastSyncRunId: string;
}

export interface AdCreativeProviderMediaReference {
  id: string;
  slotKey: string;
  kind: 'image' | 'video' | 'poster';
  providerFieldPath: string;
  url?: string;
  providerMediaId?: string;
  providerHash?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  order: number;
  providerAssetKey?: string;
  providerAssetKind?: 'image_hash' | 'image_url' | 'video_id' | 'thumbnail';
  role?: 'primary' | 'thumbnail' | 'carousel_card' | 'dynamic_candidate' | 'provider_generated';
  renditionClass: 'provider_reference' | 'provider_preview' | 'provider_source_rendition';
  mirrorState: 'not_mirrored' | 'mirrored' | 'partial';
  retrievalState?: 'mirrored' | 'metadata_only' | 'expired' | 'permission_denied' | 'invalid_media' | 'too_large' | 'failed';
  mime?: string;
  byteSize?: number;
  sha256?: string;
  storageRef?: string;
  publicUrl?: string;
  reasonCode?: string;
  limitationCode?: 'full_media_mirroring_not_implemented';
}

export interface AdCreativeProviderSourceSnapshot {
  id: string;
  projectId: string;
  sourceId: string;
  provider: 'meta';
  providerAccountId: string;
  providerCreativeId: string;
  providerIdentityKey: string;
  immutable: true;
  schemaVersion: 1;
  contentHash: string;
  providerApiVersion: string;
  providerManifest: Record<string, unknown>;
  displayProjection: {
    creativeName?: string;
    primaryText: string;
    headline: string;
    description?: string;
    ctaType?: string;
    destinationUrl?: string;
    primaryTextCandidates: string[];
    headlineCandidates: string[];
    descriptionCandidates: string[];
    carouselCardCount: number;
    presentationState: AdCreativeProviderSourceHead['presentationState'];
  };
  providerLanguageFields: Array<{ path: string; value: string; candidateScoped: boolean }>;
  mediaRefs: AdCreativeProviderMediaReference[];
  mediaMirror: {
    state: 'mirrored' | 'partial' | 'provider_reference_only';
    limitationCode?: 'full_media_mirroring_not_implemented';
    launchReady: boolean;
    mirroredCount?: number;
    partialCount?: number;
  };
  providerUpdatedAt?: string;
  sourceAsOf: string;
  createdAt: string;
}

export interface AdCreativeProviderSourceBinding {
  id: string;
  projectId: string;
  sourceId: string;
  sourceSnapshotId: string;
  provider: 'meta';
  providerAccountId: string;
  providerAdId: string;
  providerCreativeId: string;
  campaignId?: string;
  campaignName?: string;
  adSetId?: string;
  adSetName?: string;
  adName?: string;
  configuredStatus?: string;
  effectiveStatus?: string;
  previewLink?: string;
  segmentlyAssociation?: {
    state: 'linked_in_window' | 'known_segmently_destination' | 'mixed' | 'not_linked' | 'unknown';
    qualifiesForSegmentlyMedia: boolean;
    assignmentModelId: string;
    assignmentView: 'single_touch';
    window: { from: string; to: string; semantics: 'from_inclusive_to_exclusive' };
    linkageEvidence: Array<{ onboardingId?: string; funnelVersionId?: string; evidenceKind?: string }>;
    destinationEvidence: Array<{ placementId: string; alias: string; publishedUrl: string }>;
    classifiedAt: string;
    syncRunId: string;
  };
  firstObservedAt: string;
  latestObservedAt: string;
  latestMetadataSyncedAt: string;
  lastSyncRunId: string;
}

export interface AdCreativeProviderSourceLanguageEvidence {
  id: string;
  projectId: string;
  sourceId: string;
  sourceSnapshotId: string;
  sourceBindingId?: string;
  providerBaselineEvidenceId?: string;
  providerBaselineEvidenceHash?: string;
  immutable: true;
  schemaVersion: 1;
  evidenceHash: string;
  origin: AdCreativeProviderSourceLanguageOrigin;
  locale?: string;
  confidence?: number;
  mixedLanguage: boolean;
  rawProviderValues: Array<{ path: string; value: string; candidateScoped: boolean }>;
  projectionReason:
    | 'explicit_provider_value'
    | 'mixed_provider_values'
    | 'single_dynamic_candidate_not_authoritative'
    | 'no_provider_language_value'
    | 'user_confirmation';
  createdAt: string;
  createdBy?: string;
}

/**
 * A Segmently-owned detector suggestion. It is intentionally separate from
 * schema-v1 provider/user-confirmed evidence and is never authoritative input
 * for variation creation until the user confirms a project locale separately.
 */
export interface AdCreativeProviderSourceLanguageDetectionSuggestion {
  id: string;
  projectId: string;
  sourceId: string;
  sourceSnapshotId: string;
  immutable: true;
  schemaVersion: 1;
  evidenceHash: string;
  origin: 'ai_detected' | 'heuristic_detected';
  detectedLanguageCode?: string;
  projectLocaleCandidates: string[];
  /** Present only when exactly one configured project locale is unambiguous. */
  locale?: string;
  confidence?: number;
  mixedLanguage: boolean;
  projectionReason:
    | 'exact_project_locale'
    | 'ambiguous_project_locale'
    | 'no_project_locale_match'
    | 'mixed_detected_languages'
    | 'insufficient_confidence';
  evidenceFields: string[];
  inputManifestHash: string;
  detectorVersion: string;
  provider?: string;
  model?: string;
  promptVersionRefs?: string[];
  taskId?: string;
  createdAt: string;
}

export type AdCreativeProviderSourceLanguageDetectionDecision =
  | 'provider_known'
  | 'insufficient_text'
  | 'mixed'
  | 'ai_required';

interface AdCreativeProviderSourceLanguageDetectionPreflightBase {
  projectId: string;
  sourceId: string;
  snapshotId: string;
  inputManifestHash: string;
  eligibleFieldPaths: string[];
  inputCharacterCount: number;
  decision: AdCreativeProviderSourceLanguageDetectionDecision;
}

export interface AdCreativeProviderSourceLanguageDetectionProviderKnownPreflight
  extends AdCreativeProviderSourceLanguageDetectionPreflightBase {
  decision: 'provider_known';
  providerLocale?: string;
}

export interface AdCreativeProviderSourceLanguageDetectionInsufficientTextPreflight
  extends AdCreativeProviderSourceLanguageDetectionPreflightBase {
  decision: 'insufficient_text';
  minimumCharacterCount?: number;
}

export interface AdCreativeProviderSourceLanguageDetectionMixedPreflight
  extends AdCreativeProviderSourceLanguageDetectionPreflightBase {
  decision: 'mixed';
  detectedLanguageCodes?: string[];
}

export interface AdCreativeProviderSourceLanguageDetectionAiPreflight
  extends AdCreativeProviderSourceLanguageDetectionPreflightBase {
  decision: 'ai_required';
  /** Sidecar-only restoration from a durable settled candidate; no new AI or billing call. */
  recoveryMode?: 'restore_settled_candidate';
  preflightToken: string;
  issuedAt: string;
  expiresAt: string;
  model: string;
  provider: string;
  detectorVersion: string;
  costEstimate: {
    estimatedInputTokens: number;
    estimatedOutputTokens: number;
    estimatedCostUsd: number;
  };
}

export type AdCreativeProviderSourceLanguageDetectionPreflight =
  | AdCreativeProviderSourceLanguageDetectionProviderKnownPreflight
  | AdCreativeProviderSourceLanguageDetectionInsufficientTextPreflight
  | AdCreativeProviderSourceLanguageDetectionMixedPreflight
  | AdCreativeProviderSourceLanguageDetectionAiPreflight;

export interface AdCreativeProviderSourceLanguageDetectionPreflightResponse {
  success: boolean;
  data?: { preflight: AdCreativeProviderSourceLanguageDetectionPreflight };
  error?: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}

export type AdCreativeProviderSourceLanguageDetectionTaskStatus =
  | 'pending'
  | 'running'
  | 'retrying'
  | 'cancelling'
  | 'completed'
  | 'error'
  | 'cancelled'
  | 'skipped';

export interface AdCreativeProviderSourceLanguageDetectionStartResponse {
  success: boolean;
  data?: {
    taskId: string;
    status: AdCreativeProviderSourceLanguageDetectionTaskStatus;
    reused: boolean;
    snapshotId: string;
    inputManifestHash: string;
    evidenceId?: string;
  };
  error?: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}

export type AdCreativeProviderSourceLanguageDetectionSuggestionState =
  | 'available'
  | 'missing'
  | 'corrupt';

export type AdCreativeProviderSourceLanguageDetectionBillingBlockType =
  | 'missing_subscription'
  | 'missing_ai_tokens'
  | 'insufficient_credits'
  | 'quota_exhausted'
  | 'missing_model_price'
  | 'billing_config_error';

/**
 * Public-safe recovery state for the detector sidecar. Provider and billing
 * error payloads stay server-side; the UI receives only bounded action codes.
 */
export interface AdCreativeProviderSourceLanguageDetectionAttention {
  kind:
    | 'repair_required'
    | 'operator_reconciliation_required'
    | 'billing_action_required';
  code: string;
  billingBlock?: {
    type: AdCreativeProviderSourceLanguageDetectionBillingBlockType;
    reason?: string;
    meterId?: string;
    canSelfServe?: boolean;
  };
}

/** Optional detector-only projection returned with source detail. */
export interface AdCreativeProviderSourceLanguageDetectionDetail {
  snapshotId: string;
  latestTaskId?: string;
  latestTaskStatus?: AdCreativeProviderSourceLanguageDetectionTaskStatus;
  latestSuggestionState?: AdCreativeProviderSourceLanguageDetectionSuggestionState;
  latestSuggestion?: AdCreativeProviderSourceLanguageDetectionSuggestion;
  attention?: AdCreativeProviderSourceLanguageDetectionAttention;
}

export type AdCreativeProviderBaselineKind =
  | 'provider_rendition'
  | 'copy_candidate'
  | 'carousel_sequence';

export interface AdCreativeProviderBaselineSelection {
  kind: AdCreativeProviderBaselineKind;
  sourcePaths: string[];
  copy: { primaryText?: string; headline?: string; description?: string };
  mediaRefIds: string[];
  cards?: Array<{
    index: number;
    providerPath: string;
    headline?: string;
    description?: string;
    destinationUrl?: string;
    ctaType?: string;
    mediaRefIds: string[];
  }>;
  destinationUrl?: string;
  ctaType?: string;
}

export interface AdCreativeProviderBaselineChoice extends AdCreativeProviderBaselineSelection {
  id: string;
  candidateHash: string;
  label: string;
  eligible: boolean;
  blockingReasons: string[];
  media: Array<{
    id: string;
    slotKey: string;
    kind: AdCreativeProviderMediaReference['kind'];
    role?: AdCreativeProviderMediaReference['role'];
    providerFieldPath: string;
    order: number;
    mirrorState: AdCreativeProviderMediaReference['mirrorState'];
    publicUrl?: string;
    mime?: string;
    width?: number;
    height?: number;
    durationMs?: number;
    sha256?: string;
  }>;
}

export interface AdCreativeProviderBaselineEvidence {
  id: string;
  projectId: string;
  sourceId: string;
  sourceSnapshotId: string;
  sourceBindingId: string;
  immutable: true;
  schemaVersion: 1;
  evidenceHash: string;
  candidateId: string;
  candidateHash: string;
  selection: AdCreativeProviderBaselineSelection;
  providerCtaLineage?: {
    provider: 'meta';
    sourceId: string;
    sourceSnapshotId: string;
    sourceBindingId: string;
    ctaType: string;
    sourceProjectionPath: 'displayProjection.ctaType';
    localizedLabelCopied: false;
  };
  createdAt: string;
  createdBy?: string;
}

export interface AdCreativeProviderSourceDetailPageTruth {
  currentSnapshotId: string;
  limit: number;
  hasMore: boolean;
  nextCursor?: string;
}

export interface AdCreativeProviderSourceDetail {
  source: AdCreativeProviderSourceHead;
  currentSnapshot: AdCreativeProviderSourceSnapshot;
  bindings: AdCreativeProviderSourceBinding[];
  bindingsPage: AdCreativeProviderSourceDetailPageTruth;
  languageEvidence: AdCreativeProviderSourceLanguageEvidence[];
  languageEvidencePage: AdCreativeProviderSourceDetailPageTruth;
  languageDetection?: AdCreativeProviderSourceLanguageDetectionDetail;
  baselineChoices: AdCreativeProviderBaselineChoice[];
  baselineEvidence: AdCreativeProviderBaselineEvidence[];
  mediaMirror: {
    state: 'mirrored' | 'partial' | 'provider_reference_only';
    limitationCode?: 'full_media_mirroring_not_implemented';
  };
}

export interface AdCreativeProviderSourceReportKey {
  correlationKey: string;
  provider: 'meta';
  providerAccountId: string;
  providerAdId?: string;
  providerCreativeId?: string;
}

export interface ResolveAdCreativeProviderSourceReportEnrichmentRequest {
  projectId: string;
  items: readonly AdCreativeProviderSourceReportKey[];
}

export type AdCreativeProviderSourceReportUnavailableReason =
  | 'binding_not_found'
  | 'binding_invalid'
  | 'provider_key_mismatch'
  | 'source_not_found'
  | 'source_invalid'
  | 'snapshot_not_found'
  | 'snapshot_invalid'
  | 'read_failed';

export interface AdCreativeProviderSourceReportMedia {
  id: string;
  slotKey: string;
  kind: 'image' | 'video' | 'poster';
  url: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
  width: number;
  height: number;
  durationMs?: number;
}

export interface AdCreativeProviderSourceReportProjection {
  id: string;
  provider: 'meta';
  providerAccountId: string;
  providerCreativeId: string;
  snapshotId: string;
  currentSnapshotId: string;
  isCurrentSnapshot: boolean;
  creativeName?: string;
  copy: {
    primaryText?: string;
    headline?: string;
    description?: string;
  };
  cta: {
    type?: string;
    destinationUrl?: string;
  };
  presentationState: 'exact_static' | 'candidate_set' | 'provider_generated_unknown';
  capabilityState: 'full_media' | 'partial_media' | 'metadata_only' | 'dynamic_manifest';
  capabilityReasons: string[];
  sourceLanguage: {
    locale?: string;
    origin: AdCreativeProviderSourceLanguageOrigin;
    confidence?: number;
    mixedLanguage: boolean;
  };
  deploymentCount: number;
  campaignCount: number;
  lastSyncedAt: string;
  taxonomy?: {
    state: 'not_analyzed' | 'suggested' | 'reviewed' | 'rejected' | 'stale';
    reviewRequired: boolean;
    taxonomySnapshotId?: string;
    sourceSnapshotId?: string;
    topic?: { label: string; detail: string };
    angle?: { label: string; detail: string };
    concept?: { label: string; detail: string };
    hook?: { label: string; detail: string };
    proof?: { label: string; detail: string };
    format?: { label: string; detail: string };
    confidence?: number;
    warnings?: string[];
  };
  media: {
    state: 'mirrored' | 'partial' | 'provider_reference_only';
    totalCount: number;
    mirroredCount: number;
    returnedCount: number;
    truncated: boolean;
    items: AdCreativeProviderSourceReportMedia[];
  };
}

export interface AdCreativeProviderSourceReportBindingProjection {
  id: string;
  sourceSnapshotId: string;
  providerAdId: string;
  providerCreativeId: string;
  campaignId?: string;
  campaignName?: string;
  adSetId?: string;
  adSetName?: string;
  adName?: string;
  configuredStatus?: string;
  effectiveStatus?: string;
  firstObservedAt: string;
  latestObservedAt: string;
  latestMetadataSyncedAt: string;
  segmentlyAssociation?: {
    state: 'linked_in_window' | 'known_segmently_destination' | 'mixed' | 'not_linked' | 'unknown';
    qualifiesForSegmentlyMedia: boolean;
    evaluatedFrom: string;
    evaluatedTo: string;
    classifiedAt: string;
  };
}

export type AdCreativeProviderSourceReportEnrichmentItem = {
  correlationKey: string;
  provider: 'meta';
  providerAccountId: string;
  providerAdId?: string;
  providerCreativeId?: string;
} & (
  | {
    source: AdCreativeProviderSourceReportProjection;
    binding?: AdCreativeProviderSourceReportBindingProjection;
  }
  | {
    source: null;
    binding?: undefined;
    reason: AdCreativeProviderSourceReportUnavailableReason;
  }
);

export interface AdCreativeProviderSourceReportEnrichmentResult {
  items: AdCreativeProviderSourceReportEnrichmentItem[];
}

export interface AdCreativeProviderSourceReportEnrichmentResponse {
  success: boolean;
  data?: AdCreativeProviderSourceReportEnrichmentResult;
  error?: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}

export interface AdCreativeProviderSourceSyncPreflightCampaign {
  campaignId: string;
  campaignName?: string;
  ads: number;
  uniqueCreativeIds: number;
  includedBindings: number;
  includedUniqueCreativeIds: number;
  discoveryOnlyBindings: number;
  notLinked: number;
  unknown: number;
  alreadySynced: number;
  estimatedNewSources: number;
}

export interface AdCreativeProviderSourceSyncPreflight {
  preflightToken: string;
  issuedAt: string;
  expiresAt: string;
  scopeDigest: string;
  sourceAsOf: string;
  selectionSource: 'live_provider';
  freshnessState: 'fresh';
  accounts: Array<{
    accountId: string;
    accountName?: string;
    campaigns: AdCreativeProviderSourceSyncPreflightCampaign[];
  }>;
  totals: {
    campaigns: number;
    ads: number;
    uniqueCreativeIds: number;
    includedBindings: number;
    includedUniqueCreativeIds: number;
    discoveryOnlyBindings: number;
    alreadySynced: number;
    estimatedNewSources: number;
  };
  funnelAssociation: {
    scope: 'segmently_associated' | 'all';
    modelId: string;
    evaluatedFrom: string;
    evaluatedTo: string;
    state: 'complete' | 'partial' | 'unavailable';
    ads: {
      total: number;
      included: number;
      linkedInWindow: number;
      knownDestination: number;
      mixedDestinations: number;
      notLinked: number;
      unknown: number;
    };
    uniqueCreativeIds: {
      total: number;
      included: number;
    };
  };
  warnings: Array<{
    code: string;
    message: string;
    accountId?: string;
  }>;
  coordination?: {
    mode: 'all_current';
    accountCount: number;
    childRunCount: number;
  };
}

export interface AdCreativeProviderSourceSyncPreflightResponse {
  success: boolean;
  data?: { preflight: AdCreativeProviderSourceSyncPreflight };
  error?: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}

export interface AdCreativeProviderSourceSyncStartResponse {
  success: boolean;
  data?: {
    syncRunId: string;
    taskId: string;
    reused: boolean;
    status: 'pending' | 'running';
    acceptedScopeDigest: string;
    childRunCount?: number;
  };
  error?: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}

export interface AdCreativeProviderSourceSyncCounters {
  campaignsDiscovered: number;
  adsetsDiscovered: number;
  adsDiscovered: number;
  uniqueSourcesDiscovered: number;
  sourcesCommitted: number;
  sourceHeadsCreated: number;
  sourceHeadsUpdated: number;
  unchangedSources: number;
  snapshotsCreated: number;
  snapshotsReused: number;
  bindingsCommitted: number;
  bindingsQualifiedForMedia: number;
  creativesEligibleForMedia: number;
  creativesKeptMetadataOnly: number;
  sharedCreativesQualifiedByAnyBinding: number;
  unresolvedCreativeIds: number;
  mediaFilesMirrored: number;
  bytesStored: number;
  discoveryOnlyCreatives: number;
  lightweightCandidatesPersisted: number;
}

export type AdCreativeProviderSourceSyncStatus =
  | 'pending' | 'running' | 'cancelling' | 'retrying' | 'needs_reauth'
  | 'paused_quota' | 'completed' | 'failed' | 'cancelled';

export interface AdCreativeProviderSourceSyncProgress {
  syncRunId: string;
  taskId: string;
  status: AdCreativeProviderSourceSyncStatus;
  phase: string;
  outcome?: 'complete' | 'partial_success' | 'empty' | 'failed' | 'cancelled';
  progress: {
    pagesRead: Record<'campaigns' | 'adsets' | 'ads', number>;
    creativeShardsTotal: number;
    creativeShardsCompleted: number;
    counters: AdCreativeProviderSourceSyncCounters;
  };
  rateLimit?: { state: 'normal' | 'elevated' | 'high'; maxUtilizationPct?: number };
  attention?: {
    code: string;
    message: string;
    retryable: boolean;
    requiresReconnect: boolean;
    quotaRelated: boolean;
    occurredAt: string;
  };
  cancelRequested: boolean;
  canCancel: boolean;
  canResume: boolean;
  result?: {
    syncRunId: string;
    outcome: 'complete' | 'partial_success' | 'empty' | 'failed' | 'cancelled';
    accountsProcessed: number;
    campaignsProcessed: number;
    adsDiscovered: number;
    bindingsIncludedForFullSync: number;
    discoveryOnlyBindings: number;
    uniqueSourcesDiscovered: number;
    uniqueSourcesIncludedForFullSync: number;
    sourceHeadsCreated: number;
    sourceHeadsUpdated: number;
    unchangedSources: number;
    snapshotsCreated: number;
    bindingsCreatedOrUpdated: number;
    mediaFilesMirrored: number;
    bytesStored: number;
    metadataOnly: number;
    failureCountsByCode: Record<string, number>;
    retryableSourceIds: string[];
  };
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface AdCreativeProviderSourceSyncProgressResponse {
  success: boolean;
  data?: AdCreativeProviderSourceSyncProgress;
  error?: string;
}

export interface AdCreativeMetaDraft {
  id: string;
  itemId: string;
  locale: string;
  adAccountId: string;
  pageId: string;
  campaignId: string;
  adSetId: string;
  imageHash: string;
  creativeId: string;
  adId: string;
  exportManifestId: string;
  renditionId: string;
  localizationVariantId?: string;
  exportedManifestHash: string;
  providerPayloadHash: string;
  providerPayloadVersion: string;
  deploymentLocaleBindingId: string;
  deploymentKey: string;
  status?: string;
  effectiveStatus?: string;
  error?: string | null;
}

export interface CreateMetaDraftResponse {
  success: boolean;
  data?: {
    draft: AdCreativeMetaDraft;
    item: AdCreativeItem;
  };
  error?: string;
}

export interface CheckMetaDraftStatusResponse {
  success: boolean;
  data?: {
    draft: AdCreativeMetaDraft;
  };
  error?: string;
}

export interface PlatformSettingsResponse {
  success: boolean;
  data?: { settings: AdCreativePlatformSettings };
  error?: string;
}

export interface BatchMetaLaunchRequest {
  projectId: string;
  briefId: string;
  itemIds: string[];
  /** Exact authored locale selected for every item; no implicit fallback. */
  localesByItemId: Record<string, string>;
  adAccountId: string;
  pageId: string;
  campaignId: string;
  /** Existing ad set id (mode: existing) — omitted when creating a new paused ad set. */
  adSetId?: string;
  /** Name for the NEW paused ad set (mode: new, decision #3 primary path). */
  newAdSetName?: string;
  dailyBudgetCents?: number;
  fallbackApprovedByItemId?: Record<string, boolean>;
  namingConvention?: string;
  utm?: Record<string, string>;
}

export interface AdCreativeLocalizationApplyResult {
  dryRun: boolean;
  creativeItemId: string;
  targetLocale: string;
  sourceRevision: string;
  inputManifestHash: string;
  outputManifestHash: string;
  changedEntryIds: string[];
  localeRevision: number;
  /** Null when the source/default locale itself is updated. */
  variant: AdCreativeLocalizationVariant | null;
}

export interface AdCreativeImageReviewSlotBindingView {
  jobId: string;
  inputManifestHash: string;
  sourceComponentHash: string;
  reviewRef: string;
  operation: 'localize_visible_text' | 'regenerate';
  outputAssetId: string;
  outputSha256: string;
}

export interface AdCreativeImageReviewComponentBindingView {
  bindingVersion: 1;
  sourceComponentHash: string;
  targetLocale: string;
  slots: Record<string, AdCreativeImageReviewSlotBindingView>;
}

export interface AdCreativeVideoReviewSlotBindingView {
  jobId: string;
  taskId: string;
  tier: 'captions' | 'voice' | 'on_screen_text' | 'cultural_visuals';
  inputManifestHash: string;
  sourceRevision: string;
  sourceComponentHash: string;
  sourceAssetId: string;
  sourceSha256: string;
  outputAssetId: string;
  outputSha256: string;
  outputMimeType: 'video/mp4' | 'video/webm';
  outputWidth: number;
  outputHeight: number;
  outputDurationMs: number;
  captionArtifactId?: string;
  captionArtifactHash?: string;
  verificationEvidenceId: string;
  verificationEvidenceHash: string;
  outputInspectionEvidenceId: string;
  outputInspectionHash: string;
  approvedAudioArtifactId?: string;
  approvedAudioArtifactVersionHash?: string;
  approvedAudioArtifactContentHash?: string;
  audioAssetId?: string;
  audioSha256?: string;
  voiceRightsEvidenceId?: string;
  voiceRightsVersionHash?: string;
  voiceRightsContentHash?: string;
  videoTextInspectionEvidenceId?: string;
  videoTextInspectionEvidenceHash?: string;
}

export interface AdCreativeVideoReviewComponentBindingView {
  bindingVersion: 1;
  sourceComponentHash: string;
  targetLocale: string;
  slots: Record<string, AdCreativeVideoReviewSlotBindingView>;
}

/** Frontend allowlist for v3 per-component evidence pins. Historical task internals are intentionally absent. */
export type AdCreativeLocalizationVariantView = AdCreativeLocalizationVariant & {
  sourceComponentHashes: {
    text: string;
    images: string;
    video: string;
    destination: string;
  };
  componentBindings?: {
    images?: AdCreativeImageReviewComponentBindingView;
    video?: AdCreativeVideoReviewComponentBindingView;
  };
};

export interface AdCreativeLocalizationProjection {
  creativeItemId: string;
  defaultLanguage: string;
  languages: string[];
  localeRevision: number;
  sourceRevision: string;
  statusByLocale: AdCreativeItem['statusByLocale'];
  variants: AdCreativeLocalizationVariantView[];
}

export type AdCreativeLocalizationBatchComponent =
  | 'text'
  | 'images'
  | 'video'
  | 'destination';

export type AdCreativeLocalizationBatchStatus =
  | 'pending'
  | 'running'
  | 'cancelling'
  | 'cancelled'
  | 'completed'
  | 'partial'
  | 'failed';

export interface AdCreativeLocalizationBatchRequest {
  projectId: string;
  creativeItemIds: string[];
  targetLocales: string[];
  components: AdCreativeLocalizationBatchComponent[];
  destinationSelections?: Record<string, AdCreativeDestinationRef>;
  imageReuseSelections?: Record<string, string[]>;
  hardCostCapUsd: number;
  localeProfileId?: string;
  glossaryRef?: string;
  requestKey?: string;
}

export interface AdCreativeLocalizationBatchPreflight {
  preflightToken: string;
  issuedAt: string;
  expiresAt: string;
  idempotencyKey: string;
  scopeDigest: string;
  projectId: string;
  sourceItems: Array<{
    creativeItemId: string;
    sourceLocale: string;
    sourceRevision: string;
    targetLocales: string[];
  }>;
  requestedTargetLocales: string[];
  activeTargetLocales: string[];
  components: AdCreativeLocalizationBatchComponent[];
  destinationSelections?: Record<string, AdCreativeDestinationRef>;
  imageReuseSelections?: Record<string, string[]>;
  localeProfile?: { id: string; version: number; documentHash: string };
  glossary?: { id: string; version: number; documentHash: string };
  shardCount: number;
  shardCountsByComponent: Record<AdCreativeLocalizationBatchComponent, number>;
  exclusions: {
    total: number;
    samples: Array<{
      creativeItemId?: string;
      targetLocale: string;
      reason:
        | 'project_locale_archived'
        | 'source_locale'
        | 'creative_locale_archived'
        | 'creative_locale_ready';
    }>;
  };
  costEstimate: {
    currency: 'USD';
    minimumUsd: number;
    maximumUsd: number;
    hardCapUsd: number;
    perComponentCeilingUsd: Partial<Record<AdCreativeLocalizationBatchComponent, number>>;
    pricingVersions: string[];
    pricingVersionByComponent: Partial<Record<AdCreativeLocalizationBatchComponent, string>>;
  };
}

export interface AdCreativeLocalizationBatchProgress {
  projectId: string;
  batchId: string;
  taskId: string;
  status: AdCreativeLocalizationBatchStatus;
  outcome?: 'complete' | 'partial_success' | 'failed' | 'cancelled';
  scope: {
    creativeCount: number;
    targetLocales: string[];
    components: AdCreativeLocalizationBatchComponent[];
    destinationSelections?: Record<string, AdCreativeDestinationRef>;
    imageReuseSelections?: Record<string, string[]>;
    localeProfileId?: string;
    glossaryRef?: string;
  };
  progress: {
    totalShards: number;
    completedShards: number;
    failedShards: number;
    retryableFailedShards: number;
    runningShards: number;
    pendingShards: number;
    nextOrdinal: number;
    countsByComponent: Record<AdCreativeLocalizationBatchComponent, number>;
  };
  cost: {
    currency: 'USD';
    estimatedMinimumUsd: number;
    estimatedMaximumUsd: number;
    hardCapUsd: number;
    reservedUsd: number;
    actualUsd: number;
  };
  failureCountsByCode: Record<string, number>;
  exclusions: AdCreativeLocalizationBatchPreflight['exclusions'];
  delivery: {
    resumeGeneration: number;
    sequence: number;
  };
  cancelRequested: boolean;
  canCancel: boolean;
  canResume: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface AdCreativeLocalizationBatchShardResult {
  shardId: string;
  ordinal: number;
  creativeItemId: string;
  targetLocale: string;
  component: AdCreativeLocalizationBatchComponent;
  status: 'running' | 'completed' | 'failed';
  attempts: number;
  retryable?: boolean;
  disposition?: 'generated' | 'reused' | 'no_change';
  resultRef?: string;
  billingReceiptId?: string;
  errorCode?: string;
  actualCostUsd: number;
  updatedAt: string;
  completedAt?: string;
}

export interface AdCreativeLocalizationBatchShardResultPage {
  results: AdCreativeLocalizationBatchShardResult[];
  limit: number;
  hasMore: boolean;
  nextCursor?: string;
}

export interface AdCreativeLocalizationBatchPreflightResponse {
  success: boolean;
  data?: { preflight: AdCreativeLocalizationBatchPreflight };
  error?: string;
  code?: string;
  details?: Record<string, unknown>;
}

export interface AdCreativeLocalizationBatchStartResponse {
  success: boolean;
  data?: {
    batchId: string;
    taskId: string;
    status: AdCreativeLocalizationBatchStatus;
    reused: boolean;
    progress: AdCreativeLocalizationBatchProgress;
  };
  error?: string;
  code?: string;
  details?: Record<string, unknown>;
}

export interface AdCreativeLocalizationBatchProgressResponse {
  success: boolean;
  data?: { progress: AdCreativeLocalizationBatchProgress };
  error?: string;
  code?: string;
  details?: Record<string, unknown>;
}

export interface AdCreativeLocalizationBatchShardResultsResponse {
  success: boolean;
  data?: { page: AdCreativeLocalizationBatchShardResultPage };
  error?: string;
  code?: string;
  details?: Record<string, unknown>;
}

export interface BatchMetaLaunchResponse {
  success: boolean;
  data?: {
    adSetId: string;
    drafts: AdCreativeMetaDraft[];
    items: AdCreativeItem[];
    errors?: Array<{ itemId: string; error: string }>;
  };
  error?: string;
}

export interface AdCreativeApiClientOptions {
  baseUrl?: string;
  builderBaseUrl?: string;
}

function putFileToSignedUploadTarget(
  upload: { method: 'PUT'; url: string; headers: Record<string, string> },
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(upload.method, upload.url, true);
    request.timeout = 10 * 60_000;
    for (const [name, value] of Object.entries(upload.headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total <= 0) return;
      onProgress(Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100))));
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      reject(new Error(`Direct video upload failed with status ${request.status}.`));
    };
    request.onerror = () => reject(new Error('Direct video upload failed before finalization.'));
    request.onabort = () => reject(new Error('Direct video upload was cancelled.'));
    request.ontimeout = () => reject(new Error('Direct video upload timed out before finalization.'));
    request.send(file);
  });
}

export class AdCreativeApiClient {
  private readonly baseUrl: string;
  private readonly builderBaseUrl: string;

  constructor(options: AdCreativeApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ||
      import.meta.env.VITE_AGENT_API_URL ||
      import.meta.env.VITE_BUILDER_API_URL ||
      'http://localhost:8080';
    this.builderBaseUrl = options.builderBaseUrl ||
      import.meta.env.VITE_BUILDER_API_URL ||
      this.baseUrl;
  }

  async getCatalog(networkId = 'meta'): Promise<AdCreativeCatalogResponse> {
    return this.request(`/api/agent/ad-creative/networks/${encodeURIComponent(networkId)}/catalog`, {
      method: 'GET',
    });
  }

  async listDesignCollections(
    projectId: string,
    networkId = 'meta',
  ): Promise<ListAdCreativeDesignCollectionsResponse> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/agent/ad-creative/platforms/${encodeURIComponent(networkId)}/design-collections?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async saveDesignCollection(request: SaveAdCreativeDesignCollectionRequest): Promise<AdCreativeDesignCollectionResponse> {
    const networkId = encodeURIComponent(request.networkId || 'meta');
    const path = request.collectionId
      ? `/api/agent/ad-creative/platforms/${networkId}/design-collections/${encodeURIComponent(request.collectionId)}`
      : `/api/agent/ad-creative/platforms/${networkId}/design-collections`;
    return this.request(path, {
      method: request.collectionId ? 'PATCH' : 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  async generateDesignCollection(request: GenerateAdCreativeDesignCollectionRequest): Promise<GenerateAdCreativeDesignCollectionResponse> {
    const networkId = encodeURIComponent(request.networkId || 'meta');
    return this.request(`/api/agent/ad-creative/platforms/${networkId}/design-collections/generate`, {
      method: 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  async listNetworks(): Promise<ListAdCreativeNetworksResponse> {
    return this.request('/api/admin/ad-creative/networks', {
      method: 'GET',
    });
  }

  async upsertNetwork(request: UpsertAdCreativeNetworkRequest): Promise<{ success: boolean; data?: { network: AdNetwork }; error?: string }> {
    return this.request(`/api/admin/ad-creative/networks/${encodeURIComponent(request.id)}`, {
      method: 'POST',
      body: request,
    });
  }

  async getMetaAdsStatus(projectId: string): Promise<MetaAdsConnectionStatus & { success?: boolean; error?: string }> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/meta-ads/status?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async getMetaAdsOAuthUrl(projectId: string, returnTo?: string): Promise<{ url?: string; success?: boolean; error?: string }> {
    const params = new URLSearchParams({ projectId });
    if (returnTo) params.set('returnTo', returnTo);
    return this.request(`/api/meta-ads/oauth/url?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async disconnectMetaAds(projectId: string): Promise<MetaAdsConnectionStatus & { success?: boolean; error?: string }> {
    return this.request('/api/meta-ads/disconnect', {
      method: 'POST',
      body: { projectId },
      projectId,
      baseUrl: this.builderBaseUrl,
    });
  }

  async listMetaAdAccounts(projectId: string): Promise<{ accounts?: MetaAdsAdAccountSummary[]; success?: boolean; error?: string }> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/meta-ads/ad-accounts?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async listMetaPages(projectId: string): Promise<{ pages?: MetaAdsPageSummary[]; success?: boolean; error?: string }> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/meta-ads/pages?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async listMetaCampaigns(
    projectId: string,
    adAccountId: string,
    options: { limit?: number; cursor?: string } = {},
  ): Promise<MetaAdsCampaignPageResponse> {
    const call = buildProviderCampaignPageCall(projectId, adAccountId, options);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  /** Authorizes and previews an exact read-only provider-source scope. */
  async preflightMetaCreativeSourceSync(
    request: AdCreativeProviderSourceSyncPreflightRequest,
  ): Promise<AdCreativeProviderSourceSyncPreflightResponse> {
    const call = buildProviderSourceSyncPreflightCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
      // Whole-account discovery can inspect multiple explicitly selected Meta
      // accounts. Keep it bounded, but do not inherit the generic 30-second UI
      // timeout that is intended for ordinary CRUD reads and writes.
      timeoutMs: PROVIDER_SOURCE_PREFLIGHT_TIMEOUT_MS,
    });
  }

  /** Starts only the exact short-lived scope sealed by the server preflight. */
  async syncMetaCreativeSources(
    request: AdCreativeProviderSourceSyncStartRequest,
  ): Promise<AdCreativeProviderSourceSyncStartResponse> {
    const call = buildProviderSourceSyncCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async getProviderCreativeSourceSync(
    projectId: string,
    syncRunId: string,
  ): Promise<AdCreativeProviderSourceSyncProgressResponse> {
    const call = buildProviderSourceSyncProgressCall(projectId, syncRunId);
    return this.request(call.path, { method: call.method, projectId: call.projectId });
  }

  async cancelProviderCreativeSourceSync(
    projectId: string,
    syncRunId: string,
  ): Promise<AdCreativeProviderSourceSyncProgressResponse> {
    const call = buildProviderSourceSyncCancelCall(projectId, syncRunId);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async resumeProviderCreativeSourceSync(
    projectId: string,
    syncRunId: string,
  ): Promise<AdCreativeProviderSourceSyncProgressResponse> {
    const call = buildProviderSourceSyncResumeCall(projectId, syncRunId);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async listProviderCreativeSources(
    projectId: string,
    options: { providerAccountId?: string; limit?: number; cursor?: string } = {},
  ): Promise<{
    success: boolean;
    data?: {
      sources: AdCreativeProviderSourceHead[];
      limit: number;
      hasMore: boolean;
      nextCursor?: string;
    };
    error?: string;
  }> {
    const call = buildProviderSourceListCall(projectId, options);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async listProviderCreativeSourceCandidates(
    projectId: string,
    providerAccountId: string,
    options: { limit?: number; cursor?: string } = {},
  ): Promise<{
    success: boolean;
    data?: {
      candidates: AdCreativeProviderSourceCandidate[];
      limit: number;
      hasMore: boolean;
      nextCursor?: string;
    };
    error?: string;
    code?: string;
    status?: number;
    details?: Record<string, unknown>;
  }> {
    const call = buildProviderSourceCandidateListCall(projectId, providerAccountId, options);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async preflightProviderCreativeSourceCandidatePromotion(
    request: AdCreativeProviderSourceCandidatePromotionPreflightRequest,
  ): Promise<{
    success: boolean;
    data?: {
      candidateIds: string[];
      preflight: AdCreativeProviderSourceSyncPreflight;
    };
    error?: string;
    code?: string;
    status?: number;
    details?: Record<string, unknown>;
  }> {
    const call = buildProviderSourceCandidatePromotionPreflightCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async preflightProviderCreativeSourceReportRow(
    request: AdCreativeProviderSourceReportRowPreflightRequest,
  ): Promise<{
    success: boolean;
    data?: { preflight: AdCreativeProviderSourceSyncPreflight };
    error?: string;
    code?: string;
    status?: number;
    details?: Record<string, unknown>;
  }> {
    const call = buildProviderSourceReportRowPreflightCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async resolveProviderCreativeSourcesByProviderKeys(
    request: ResolveAdCreativeProviderSourceReportEnrichmentRequest,
  ): Promise<AdCreativeProviderSourceReportEnrichmentResponse> {
    return this.request('/api/agent/ad-creative/sources/resolve-by-provider-keys', {
      method: 'POST',
      body: {
        projectId: request.projectId,
        items: request.items,
      },
      projectId: request.projectId,
    });
  }

  async getProviderCreativeSource(
    projectId: string,
    sourceId: string,
  ): Promise<{ success: boolean; data?: AdCreativeProviderSourceDetail; error?: string }> {
    const call = buildProviderSourceDetailCall(projectId, sourceId);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async listProviderCreativeSourceBindings(
    projectId: string,
    sourceId: string,
    options: { limit?: number; cursor?: string } = {},
  ): Promise<{
    success: boolean;
    data?: AdCreativeProviderSourceDetailPageTruth & {
      bindings: AdCreativeProviderSourceBinding[];
    };
    error?: string;
  }> {
    const call = buildProviderSourceBindingsCall(projectId, sourceId, options);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async listProviderCreativeSourceLanguageEvidence(
    projectId: string,
    sourceId: string,
    options: { limit?: number; cursor?: string } = {},
  ): Promise<{
    success: boolean;
    data?: AdCreativeProviderSourceDetailPageTruth & {
      languageEvidence: AdCreativeProviderSourceLanguageEvidence[];
    };
    error?: string;
  }> {
    const call = buildProviderSourceLanguageEvidenceCall(projectId, sourceId, options);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  /** Pure exact-snapshot gate/cost preflight; this never starts detection. */
  async preflightProviderCreativeSourceLanguageDetection(
    request: AdCreativeProviderSourceLanguageDetectionPreflightRequest,
  ): Promise<AdCreativeProviderSourceLanguageDetectionPreflightResponse> {
    const call = buildProviderSourceLanguageDetectionPreflightCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  /** Starts the detector only for the exact short-lived preflight input. */
  async startProviderCreativeSourceLanguageDetection(
    request: AdCreativeProviderSourceLanguageDetectionStartRequest,
  ): Promise<AdCreativeProviderSourceLanguageDetectionStartResponse> {
    const call = buildProviderSourceLanguageDetectionStartCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async preflightProviderCreativeSourceTaxonomy(
    request: AdCreativeProviderSourceTaxonomyPreflightRequest,
  ): Promise<{
    success: boolean;
    data?: { preflight: AdCreativeProviderSourceTaxonomyPreflight };
    error?: string;
  }> {
    const call = buildProviderSourceTaxonomyPreflightCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async startProviderCreativeSourceTaxonomy(
    request: AdCreativeProviderSourceTaxonomyStartRequest,
  ): Promise<{
    success: boolean;
    data?: { taskId: string; status: string; reused: boolean };
    error?: string;
  }> {
    const call = buildProviderSourceTaxonomyStartCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async getProviderCreativeSourceTaxonomy(
    projectId: string,
    sourceId: string,
  ): Promise<{ success: boolean; data?: AdCreativeProviderSourceTaxonomyCurrent; error?: string }> {
    const call = buildProviderSourceTaxonomyReadCall(projectId, sourceId);
    return this.request(call.path, { method: call.method, projectId: call.projectId });
  }

  async reviewProviderCreativeSourceTaxonomy(
    request: AdCreativeProviderSourceTaxonomyReviewRequest,
  ): Promise<{
    success: boolean;
    data?: { taxonomy: AdCreativeProviderSourceTaxonomySnapshot };
    error?: string;
  }> {
    const call = buildProviderSourceTaxonomyReviewCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async confirmProviderCreativeSourceLanguage(request: {
    projectId: string;
    sourceId: string;
    snapshotId: string;
    sourceBindingId: string;
    providerBaselineEvidenceId?: string;
    locale: string;
  }): Promise<{
    success: boolean;
    data?: {
      source: AdCreativeProviderSourceHead;
      evidence: AdCreativeProviderSourceLanguageEvidence;
      reused: boolean;
    };
    error?: string;
  }> {
    const call = buildProviderSourceLanguageConfirmationCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async selectProviderCreativeSourceBaseline(request: {
    projectId: string;
    sourceId: string;
    snapshotId: string;
    sourceBindingId: string;
    candidateId: string;
    candidateHash: string;
  }): Promise<{
    success: boolean;
    data?: { evidence: AdCreativeProviderBaselineEvidence; reused: boolean };
    error?: string;
  }> {
    const call = buildProviderSourceBaselineSelectionCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async createProviderCreativeVariationDraft(request: {
    projectId: string;
    sourceId: string;
    snapshotId: string;
    sourceBindingId: string;
    providerBaselineEvidenceId?: string;
    sourceLocale: string;
    targetLocales?: string[];
    idempotencyKey?: string;
  }): Promise<{
    success: boolean;
    data?: {
      item: AdCreativeItem;
      reused: boolean;
      sourceSnapshotId: string;
      sourceBindingId: string;
      providerBaselineEvidenceId?: string;
      mediaMirror: AdCreativeProviderSourceDetail['mediaMirror'];
      generatedAssetCount: 0;
      providerMutationCount: 0;
    };
    error?: string;
  }> {
    const call = buildProviderSourceVariationDraftCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  /**
   * Ad-level Marketing-API insights read (U5): daily rows with impressions,
   * clicks, spend, ctr, frequency, actions per ad for the given window.
   */
  async getMetaAdInsights(
    projectId: string,
    adAccountId: string,
    options: { since: string; until: string; limit?: number } ,
  ): Promise<{ success?: boolean; rows?: Array<Record<string, unknown>>; error?: string; data?: { rows?: Array<Record<string, unknown>> } }> {
    return this.request('/api/meta-ads/insights-preview', {
      method: 'POST',
      body: { projectId, adAccountId, since: options.since, until: options.until, limit: options.limit ?? 500 },
      projectId,
    });
  }

  async listMetaAdSets(projectId: string, adAccountId: string, campaignId?: string): Promise<{ adsets?: MetaAdsAdSetSummary[]; success?: boolean; error?: string }> {
    const params = new URLSearchParams({ projectId, adAccountId });
    if (campaignId) params.set('campaignId', campaignId);
    return this.request(`/api/meta-ads/adsets?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async createCampaign(request: CreateAdCreativeCampaignRequest): Promise<CampaignResponse> {
    return this.request('/api/agent/ad-creative/campaigns', {
      method: 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  async listCampaigns(projectId: string, options: { limit?: number } = {}): Promise<ListCampaignsResponse> {
    const params = new URLSearchParams({ projectId });
    if (options.limit) params.set('limit', String(options.limit));
    return this.request(`/api/agent/ad-creative/campaigns?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async createBrief(request: CreateAdCreativeBriefRequest): Promise<BriefResponse> {
    return this.request('/api/agent/ad-creative/briefs', {
      method: 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  /**
   * Prompt-bar happy path (Create home): creates the brief synchronously and
   * chains angle generation + the angles x formats creative generation on the
   * server. Returns the brief immediately so the UI can land on ?batch=<id>.
   */
  async generateBatch(request: {
    projectId: string;
    /** Optional when an insight selection or derived context is provided (FR3 follow-up). */
    promptText?: string;
    templateIds: string[];
    /** FR4: catalog network the templates belong to (validated server-side). */
    networkId?: string;
    /** FR4: explicit model/effort/budget overrides for the text agents. */
    generation?: { textModel?: string; reasoningEffort?: 'low' | 'medium' | 'high'; maxOutputTokens?: number };
    title?: string;
    objective?: string;
    audienceName?: string;
    painOrNeed?: string;
    offerPromise?: string;
    proofSummary?: string;
    cta?: string;
    destinationUrl?: string;
    designCollectionId?: string;
    angleCount?: number;
    imageModel?: string;
    userInstruction?: string;
    sourceRefs?: Record<string, unknown>;
    /** Pinned insights analysis grounding the batch (insight-context rework). */
    insightsAnalysisId?: string;
    /** Derived/edited context chips (job maps to brief.task.jobToBeDone). */
    jobToBeDone?: string;
    /** FR3: manual thesis subset persisted on the created brief. */
    selectedInsightIds?: string[];
    insightSelectionMode?: 'auto' | 'manual';
    /** FR3: seed theses forwarded into the chained angles call. */
    seedInsightIds?: string[];
    /** FR7: chosen creative frameworks (validated against the served catalog). */
    frameworkIds?: string[];
    /** FR7: user's own creative description — peer alternative to frameworks. */
    customCreativeBrief?: string;
    /** FR8: briefs_first stops the chain after draft creative briefs land. */
    mode?: 'direct' | 'briefs_first';
    /** Review-gated, server-fingerprinted Creative Decisions refresh lineage. */
    creativeDecisionHandoff?: CreativeDecisionRefreshHandoffPackage;
  }): Promise<BriefResponse> {
    return this.request('/api/agent/ad-creative/briefs/generate-batch', {
      method: 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  async generateBriefTopics(request: GenerateAdCreativeBriefTopicsRequest): Promise<GenerateBriefTopicsResponse> {
    return this.request('/api/agent/ad-creative/briefs/generate-topics', {
      method: 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  /** FR8: review-stage read of a generation's draft creative briefs. */
  async getBriefGeneration(projectId: string, generationId: string): Promise<{
    success: boolean;
    data?: { generation: Record<string, unknown>; drafts: AdCreativeBriefDraft[] };
    error?: string;
  }> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/agent/ad-creative/brief-generations/${encodeURIComponent(generationId)}?${params.toString()}`, {
      method: 'GET',
    });
  }

  /** FR8: per-draft edit — the edited draft REPLACES the generated one. */
  async updateBriefDrafts(request: {
    projectId: string;
    generationId: string;
    drafts: Array<{ id: string; data: AdCreativeBriefDraft['data'] }>;
  }): Promise<{ success: boolean; data?: { updatedCount: number }; error?: string }> {
    return this.request(`/api/agent/ad-creative/brief-generations/${encodeURIComponent(request.generationId)}/apply`, {
      method: 'POST',
      body: { projectId: request.projectId, updatedDrafts: request.drafts },
      projectId: request.projectId,
    });
  }

  /** FR8: start creative generation for accepted drafts (creative-brief generations). */
  async generateFromDrafts(request: {
    projectId: string;
    generationId: string;
    draftIds: string[];
  }): Promise<{ success: boolean; data?: { tasksByDraft: Record<string, string> }; error?: string }> {
    return this.request(`/api/agent/ad-creative/brief-generations/${encodeURIComponent(request.generationId)}/apply`, {
      method: 'POST',
      body: { projectId: request.projectId, acceptedItemIds: request.draftIds },
      projectId: request.projectId,
    });
  }

  async applyBriefTopics(request: {
    projectId: string;
    generationId: string;
    acceptedItemIds: string[];
  }): Promise<ApplyBriefTopicsResponse> {
    return this.request(`/api/agent/ad-creative/brief-generations/${encodeURIComponent(request.generationId)}/apply`, {
      method: 'POST',
      body: {
        projectId: request.projectId,
        acceptedItemIds: request.acceptedItemIds,
      },
      projectId: request.projectId,
    });
  }

  async getBrief(projectId: string, briefId: string): Promise<BriefResponse> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/agent/ad-creative/briefs/${encodeURIComponent(briefId)}?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async listBriefs(projectId: string, options: { networkId?: string; limit?: number } = {}): Promise<ListBriefsResponse> {
    const params = new URLSearchParams({ projectId });
    if (options.networkId) params.set('networkId', options.networkId);
    if (options.limit) params.set('limit', String(options.limit));
    return this.request(`/api/agent/ad-creative/briefs?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async updateBrief(
    projectId: string,
    briefId: string,
    patch: Partial<CreateAdCreativeBriefRequest> & Record<string, unknown>,
  ): Promise<BriefResponse> {
    return this.request(`/api/agent/ad-creative/briefs/${encodeURIComponent(briefId)}`, {
      method: 'PATCH',
      body: { ...patch, projectId },
      projectId,
    });
  }

  async archiveBrief(projectId: string, briefId: string): Promise<BriefResponse> {
    return this.request(`/api/agent/ad-creative/briefs/${encodeURIComponent(briefId)}/archive`, {
      method: 'POST',
      body: { projectId },
      projectId,
    });
  }

  async createPlan(projectId: string, briefId: string, request: { templateIds?: string[]; variantBudget?: number } = {}): Promise<PlanResponse> {
    return this.request(`/api/agent/ad-creative/briefs/${encodeURIComponent(briefId)}/plan`, {
      method: 'POST',
      body: { ...request, projectId },
      projectId,
    });
  }

  async createResearchRun(
    projectId: string,
    campaignId: string | undefined,
    briefId: string,
    body: { focusKind?: string; userInstruction?: string } = {},
  ): Promise<CreateResearchResponse> {
    return this.request(`/api/agent/ad-creative/briefs/${encodeURIComponent(briefId)}/research`, {
      method: 'POST',
      body: { ...body, projectId, campaignId },
      projectId,
    });
  }

  async generateAngles(
    projectId: string,
    campaignId: string | undefined,
    briefId: string,
    body: { count?: number; mode?: string; userInstruction?: string; seedInsightIds?: string[]; insightsAnalysisId?: string; generation?: { textModel?: string; reasoningEffort?: 'low' | 'medium' | 'high'; maxOutputTokens?: number } } = {},
  ): Promise<GenerateAnglesResponse> {
    return this.request(`/api/agent/ad-creative/briefs/${encodeURIComponent(briefId)}/generate-angles`, {
      method: 'POST',
      body: { ...body, projectId, campaignId },
      projectId,
    });
  }

  async generate(request: GenerateAdCreativeRequest): Promise<GenerateAdCreativeResponse> {
    return this.request('/api/agent/ad-creative/generate', {
      method: 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  async generateTask(request: GenerateAdCreativeRequest): Promise<GenerateAdCreativeTaskResponse> {
    return this.request('/api/agent/ad-creative/generate-task', {
      method: 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  async listItems(projectId: string, options: { networkId?: string; runId?: string; briefId?: string; limit?: number; abortKey?: string } = {}): Promise<ListAdCreativeItemsResponse> {
    const params = new URLSearchParams({ projectId });
    if (options.networkId) params.set('networkId', options.networkId);
    if (options.runId) params.set('runId', options.runId);
    if (options.briefId) params.set('briefId', options.briefId);
    if (options.limit) params.set('limit', String(options.limit));
    return this.request(`/api/agent/ad-creative/items?${params.toString()}`, {
      method: 'GET',
      projectId,
      abortKey: options.abortKey,
    });
  }

  async createManifestVariationChild(
    request: CreateAdCreativeManifestVariationRequest,
  ): Promise<CreateAdCreativeManifestVariationResponse> {
    const call = buildManifestVariationCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async createLaunchPlan(
    projectId: string,
    campaignId: string,
    setId: string,
    body: { runId?: string; itemIds?: string[]; dailyBudget?: number; budgetMode?: string; schedule?: Record<string, unknown> } = {},
  ): Promise<LaunchPlanResponse> {
    return this.request(`/api/agent/ad-creative/sets/${encodeURIComponent(setId)}/create-launch-plan`, {
      method: 'POST',
      body: { ...body, projectId, campaignId },
      projectId,
    });
  }

  async exportLaunchPlan(
    projectId: string,
    launchPlanId: string,
    body: { mode?: string } = {},
  ): Promise<LaunchExportResponse> {
    return this.request(`/api/agent/ad-creative/launch-plans/${encodeURIComponent(launchPlanId)}/export`, {
      method: 'POST',
      body: { ...body, projectId },
      projectId,
    });
  }

  async importMetrics(
    projectId: string,
    campaignId: string,
    launchPlanId: string,
    body: {
      metricSource?: string;
      metricMode?: 'operator' | 'fixture';
      metrics?: Array<Record<string, unknown>>;
    } = {},
  ): Promise<MetricsImportResponse> {
    return this.request(`/api/agent/ad-creative/launch-plans/${encodeURIComponent(launchPlanId)}/import-metrics`, {
      method: 'POST',
      body: { ...body, projectId, campaignId },
      projectId,
    });
  }

  async improveCampaign(
    projectId: string,
    campaignId: string,
    body: { learningSnapshotId?: string; preserveWinnerRefs?: string[] } = {},
  ): Promise<ImproveCampaignResponse> {
    return this.request(`/api/agent/ad-creative/campaigns/${encodeURIComponent(campaignId)}/improve`, {
      method: 'POST',
      body: { ...body, projectId },
      projectId,
    });
  }

  async getTask(projectId: string, taskId: string): Promise<GetAdCreativeTaskResponse> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/agent/ad-creative/tasks/${encodeURIComponent(taskId)}?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async cancelTask(
    projectId: string,
    taskId: string,
    reason = 'User cancelled video transcription',
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return this.request(`/api/agent/task/${encodeURIComponent(taskId)}/cancel`, {
      method: 'POST',
      body: { projectId, reason },
      projectId,
    });
  }

  async appendFeedback(
    projectId: string,
    itemId: string,
    feedback: AdCreativePerformanceFeedback,
  ): Promise<{ success: boolean; data?: { item: AdCreativeItem }; error?: string }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/feedback`, {
      method: 'POST',
      body: { projectId, feedback },
      projectId,
    });
  }

  async updateItem(
    projectId: string,
    itemId: string,
    body: {
      review?: Record<string, unknown>;
    },
  ): Promise<{ success: boolean; data?: { item: AdCreativeItem }; error?: string }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}`, {
      method: 'PATCH',
      body: { ...body, projectId },
      projectId,
    });
  }

  async preflightLocalizationBatch(
    request: AdCreativeLocalizationBatchRequest,
  ): Promise<AdCreativeLocalizationBatchPreflightResponse> {
    return this.request('/api/agent/ad-creative/localization-batches/preflight', {
      method: 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  async startLocalizationBatch(
    request: AdCreativeLocalizationBatchRequest & { preflightToken: string },
  ): Promise<AdCreativeLocalizationBatchStartResponse> {
    return this.request('/api/agent/ad-creative/localization-batches', {
      method: 'POST',
      body: request,
      projectId: request.projectId,
    });
  }

  async getLocalizationBatch(
    projectId: string,
    batchId: string,
  ): Promise<AdCreativeLocalizationBatchProgressResponse> {
    const params = new URLSearchParams({ projectId });
    return this.request(
      `/api/agent/ad-creative/localization-batches/${encodeURIComponent(batchId)}?${params.toString()}`,
      { method: 'GET', projectId },
    );
  }

  async listLocalizationBatchShardResults(
    projectId: string,
    batchId: string,
    options: { limit?: number; cursor?: string } = {},
  ): Promise<AdCreativeLocalizationBatchShardResultsResponse> {
    const params = new URLSearchParams({ projectId });
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.cursor) params.set('cursor', options.cursor);
    return this.request(
      `/api/agent/ad-creative/localization-batches/${encodeURIComponent(batchId)}/shards?${params.toString()}`,
      { method: 'GET', projectId },
    );
  }

  async cancelLocalizationBatch(
    projectId: string,
    batchId: string,
  ): Promise<AdCreativeLocalizationBatchProgressResponse> {
    return this.request(
      `/api/agent/ad-creative/localization-batches/${encodeURIComponent(batchId)}/cancel`,
      { method: 'POST', body: { projectId }, projectId },
    );
  }

  async resumeLocalizationBatch(
    projectId: string,
    batchId: string,
  ): Promise<AdCreativeLocalizationBatchProgressResponse> {
    return this.request(
      `/api/agent/ad-creative/localization-batches/${encodeURIComponent(batchId)}/resume`,
      { method: 'POST', body: { projectId }, projectId },
    );
  }

  async addOrRestoreItemLocale(
    projectId: string,
    itemId: string,
    locale: string,
    sourceLocale?: string,
  ): Promise<{
    success: boolean;
    data?: { item: AdCreativeItem; variant: AdCreativeLocalizationVariant | null; restored: boolean };
    error?: string;
  }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/locales`, {
      method: 'POST',
      body: { projectId, locale, sourceLocale },
      projectId,
    });
  }

  async archiveItemLocale(
    projectId: string,
    itemId: string,
    locale: string,
  ): Promise<{
    success: boolean;
    data?: { item: AdCreativeItem; variant: AdCreativeLocalizationVariant | null };
    error?: string;
  }> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/locales/${encodeURIComponent(locale)}?${params.toString()}`, {
      method: 'DELETE',
      projectId,
    });
  }

  async changeItemDefaultLocale(
    projectId: string,
    itemId: string,
    locale: string,
  ): Promise<{
    success: boolean;
    data?: {
      item: AdCreativeItem;
      variants: AdCreativeLocalizationVariant[];
      changed: boolean;
      previousDefaultLanguage: string;
    };
    error?: string;
    code?: string;
  }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/default-locale`, {
      method: 'PATCH',
      body: { projectId, locale },
      projectId,
    });
  }

  async listItemLocalizations(
    projectId: string,
    itemId: string,
  ): Promise<{ success: boolean; data?: { localizations: AdCreativeLocalizationProjection }; error?: string }> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async reuseItemSourceMediaForAllLocales(
    projectId: string,
    itemId: string,
    slotKey: string,
  ): Promise<{
    success: boolean;
    data?: {
      item: AdCreativeItem;
      variants: AdCreativeLocalizationVariant[];
      slotKey: string;
      appliedLocales: string[];
      changedLocales: string[];
    };
    error?: string;
  }> {
    return this.request(
      `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations/media/reuse-source-for-all`,
      {
        method: 'POST',
        body: { projectId, slotKey },
        projectId,
      },
    );
  }

  async uploadItemLocalizationImage(
    projectId: string,
    itemId: string,
    targetLocale: string,
    slotKey: string,
    file: File,
  ): Promise<AdCreativeLocalizationImageUploadResponse> {
    const params = new URLSearchParams({ projectId });
    const formData = new FormData();
    formData.set('targetLocale', targetLocale);
    formData.set('slotKey', slotKey);
    formData.set('file', file, file.name);
    return this.request(
      `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations/media/upload-image?${params.toString()}`,
      {
        method: 'POST',
        formData,
        projectId,
      },
    );
  }

  async uploadItemLocalizationVideo(
    projectId: string,
    itemId: string,
    targetLocale: string,
    slotKey: string,
    file: File,
  ): Promise<AdCreativeLocalizationVideoUploadResponse> {
    const params = new URLSearchParams({ projectId });
    const formData = new FormData();
    formData.set('targetLocale', targetLocale);
    formData.set('slotKey', slotKey);
    formData.set('file', file, file.name);
    return this.request(
      `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations/media/upload-video?${params.toString()}`,
      {
        method: 'POST',
        formData,
        projectId,
        timeoutMs: 5 * 60_000,
      },
    );
  }

  async uploadItemVideoAudioCandidate(
    projectId: string,
    itemId: string,
    fields: AdCreativeApprovedAudioCandidateUploadFields,
    file: File,
  ): Promise<AdCreativeApprovedAudioCandidateUploadResponse> {
    const formData = new FormData();
    formData.set('targetLocale', fields.targetLocale);
    formData.set('slotKey', fields.slotKey);
    formData.set('captionArtifactId', fields.captionArtifactId);
    formData.set('voiceRightsEvidenceId', fields.voiceRightsEvidenceId);
    formData.set('file', file, file.name);
    return this.request(
      buildAdCreativeApprovedAudioCandidateUploadPath(projectId, itemId),
      {
        method: 'POST',
        formData,
        projectId,
        timeoutMs: 5 * 60_000,
      },
    );
  }

  async listItemVideoAudioCandidates(
    projectId: string,
    itemId: string,
    options: { limit?: number; cursor?: string } = {},
  ): Promise<AdCreativeApprovedAudioCandidateListResponse> {
    return this.request(
      buildAdCreativeApprovedAudioCandidateListPath(projectId, itemId, options),
      { method: 'GET', projectId },
    );
  }

  async getItemVideoAudioCandidate(
    projectId: string,
    itemId: string,
    candidateId: string,
  ): Promise<AdCreativeApprovedAudioCandidateReadResponse> {
    return this.request(
      buildAdCreativeApprovedAudioCandidateReadPath(projectId, itemId, candidateId),
      { method: 'GET', projectId },
    );
  }

  async loadItemVideoAudioCandidatePlayback(
    projectId: string,
    itemId: string,
    candidateId: string,
  ): Promise<AdCreativeBinaryTransportResponse> {
    return this.transport.requestBinary({
      path: buildAdCreativeApprovedAudioCandidatePlaybackPath(projectId, itemId, candidateId),
      method: 'GET',
      baseUrl: this.baseUrl,
      projectId,
      abortKey: `ad-creative-approved-audio-playback:${projectId}:${itemId}`,
      timeoutMs: 60_000,
    });
  }

  async reviewItemVideoAudioCandidate(
    projectId: string,
    itemId: string,
    candidateId: string,
    fields: AdCreativeApprovedAudioCandidateReviewFields,
  ): Promise<AdCreativeApprovedAudioCandidateReviewResponse> {
    return this.request(
      buildAdCreativeApprovedAudioCandidateReviewPath(projectId, itemId, candidateId),
      {
        method: 'POST',
        body: fields,
        projectId,
        timeoutMs: 2 * 60_000,
      },
    );
  }

  async createItemLocalizationVideoUploadIntent(
    projectId: string,
    itemId: string,
    targetLocale: string,
    slotKey: string,
    file: File,
  ): Promise<AdCreativeLocalizationVideoUploadIntentResponse> {
    return this.request(
      `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations/media/video-upload-intents`,
      {
        method: 'POST',
        body: {
          projectId,
          targetLocale,
          slotKey,
          mimeType: file.type,
          byteSize: file.size,
        },
        projectId,
      },
    );
  }

  async finalizeItemLocalizationVideoUploadIntent(
    projectId: string,
    itemId: string,
    targetLocale: string,
    slotKey: string,
    intentId: string,
  ): Promise<AdCreativeLocalizationVideoUploadResponse> {
    return this.request(
      `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations/media/video-upload-intents/${encodeURIComponent(intentId)}/finalize`,
      {
        method: 'POST',
        body: { projectId, targetLocale, slotKey },
        projectId,
        timeoutMs: 10 * 60_000,
      },
    );
  }

  async uploadItemLocalizationVideoStaged(
    projectId: string,
    itemId: string,
    targetLocale: string,
    slotKey: string,
    file: File,
    onProgress?: (progress: AdCreativeStagedVideoUploadProgress) => void,
  ): Promise<AdCreativeLocalizationVideoUploadResponse> {
    onProgress?.({ phase: 'preparing', percent: 0 });
    const intent = await this.createItemLocalizationVideoUploadIntent(
      projectId,
      itemId,
      targetLocale,
      slotKey,
      file,
    );
    if (!intent.success || !intent.data) {
      return { success: false, error: intent.error, code: intent.code };
    }
    await putFileToSignedUploadTarget(intent.data.upload, file, (percent) => {
      onProgress?.({ phase: 'uploading', percent });
    });
    onProgress?.({ phase: 'finalizing', percent: 100 });
    return this.finalizeItemLocalizationVideoUploadIntent(
      projectId,
      itemId,
      targetLocale,
      slotKey,
      intent.data.intentId,
    );
  }

  async preflightItemLocalization(
    request: AdCreativeLocalizationPreflightRequest,
  ): Promise<AdCreativeLocalizationPreflightResponse> {
    const call = buildLocalizationPreflightCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async inspectItemLocalizationSourceImages(
    request: AdCreativeSourceImageInspectionRequest,
  ): Promise<AdCreativeSourceImageInspectionResponse> {
    const call = buildSourceImageInspectionCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async startItemLocalizationJob(
    request: AdCreativeLocalizationStartRequest,
  ): Promise<AdCreativeLocalizationStartResponse> {
    const call = buildLocalizationStartCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async getLocalizationJob(
    projectId: string,
    jobId: string,
    component: AdCreativeLocalizationComponent = 'text',
  ): Promise<AdCreativeLocalizationJobResponse> {
    const call = buildLocalizationJobReadCall(projectId, jobId, component);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async getCaptionReview(
    projectId: string,
    jobId: string,
    targetLocale: string,
    slotKey: string,
  ): Promise<AdCreativeCaptionReviewResponse> {
    const call = buildCaptionReviewReadCall(projectId, jobId, targetLocale, slotKey);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async getImageLocalizationReview(
    projectId: string,
    itemId: string,
    jobId: string,
    targetLocale: string,
    reviewRef?: string,
  ): Promise<AdCreativeImageReviewResponse> {
    const call = buildImageReviewReadCall(projectId, itemId, jobId, targetLocale, reviewRef);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async listVideoTranscripts(
    request: AdCreativeVideoTranscriptListRequest,
  ): Promise<AdCreativeVideoTranscriptListResponse> {
    const call = buildVideoTranscriptListCall(request);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async getVideoTranscript(
    request: AdCreativeVideoTranscriptReadRequest,
  ): Promise<AdCreativeVideoTranscriptReadResponse> {
    const call = buildVideoTranscriptReadCall(request);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async createVideoTranscript(
    request: AdCreativeVideoTranscriptCreateRequest,
  ): Promise<AdCreativeVideoTranscriptStartResponse> {
    const call = buildVideoTranscriptCreateCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async reviewVideoTextInspection(
    request: AdCreativeVideoTextInspectionReviewRequest,
  ): Promise<AdCreativeVideoTextInspectionReviewResponse> {
    const call = buildVideoTextInspectionReviewCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async listVideoTextInspections(
    request: AdCreativeVideoTextInspectionListRequest,
  ): Promise<AdCreativeVideoTextInspectionListResponse> {
    const call = buildVideoTextInspectionListCall(request);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async reviewCulturalVisualEvidence(
    request: AdCreativeCulturalVisualReviewRequest,
  ): Promise<AdCreativeCulturalVisualReviewResponse> {
    const call = buildCulturalVisualReviewCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async listCulturalVisualEvidence(
    request: AdCreativeCulturalVisualListRequest,
  ): Promise<AdCreativeCulturalVisualListResponse> {
    const call = buildCulturalVisualListCall(request);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async importManualVideoTranscript(
    request: AdCreativeManualVideoTranscriptImportRequest,
  ): Promise<AdCreativeVideoTranscriptMutationResponse> {
    const call = buildManualVideoTranscriptImportCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async selectVideoTranscript(
    request: AdCreativeVideoTranscriptSelectRequest,
  ): Promise<AdCreativeVideoTranscriptMutationResponse> {
    const call = buildVideoTranscriptSelectCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async listItemVideoVoiceRights(
    request: AdCreativeVideoVoiceRightsListRequest,
  ): Promise<AdCreativeVideoVoiceRightsListResponse> {
    const call = buildVideoVoiceRightsListCall(request);
    return this.request(call.path, {
      method: call.method,
      projectId: call.projectId,
    });
  }

  async approveItemVideoVoiceRights(
    request: AdCreativeVideoVoiceRightsApproveRequest,
  ): Promise<AdCreativeVideoVoiceRightsMutationResponse> {
    const call = buildVideoVoiceRightsApproveCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async revokeItemVideoVoiceRights(
    request: AdCreativeVideoVoiceRightsRevokeRequest,
  ): Promise<AdCreativeVideoVoiceRightsMutationResponse> {
    const call = buildVideoVoiceRightsRevokeCall(request);
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async cancelLocalizationJob(
    projectId: string,
    jobId: string,
    component: AdCreativeLocalizationComponent,
  ): Promise<AdCreativeLocalizationJobMutationResponse> {
    const call = buildLocalizationJobMutationCall(projectId, jobId, component, 'cancel');
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async resumeLocalizationJob(
    projectId: string,
    jobId: string,
    component: AdCreativeLocalizationComponent,
  ): Promise<AdCreativeLocalizationJobMutationResponse> {
    const call = buildLocalizationJobMutationCall(projectId, jobId, component, 'resume');
    return this.request(call.path, {
      method: call.method,
      body: call.body,
      projectId: call.projectId,
    });
  }

  async exportItemLocalizationManifest(
    projectId: string,
    itemId: string,
    sourceLocale?: string,
  ): Promise<{ success: boolean; data?: { manifest: AdCreativeLocalizationManifest }; error?: string }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations/export`, {
      method: 'POST',
      body: { projectId, sourceLocale },
      projectId,
    });
  }

  async applyItemLocalization(
    projectId: string,
    itemId: string,
    targetLocale: string,
    manifest: AdCreativeLocalizationManifest,
    operations: AdCreativeLocalizationOperation[],
    options: { dryRun?: boolean } = {},
  ): Promise<{ success: boolean; data?: { result: AdCreativeLocalizationApplyResult }; error?: string }> {
    return this.request(
      `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations/${encodeURIComponent(targetLocale)}/apply`,
      {
        method: 'POST',
        body: { projectId, manifest, operations, dryRun: Boolean(options.dryRun) },
        projectId,
      },
    );
  }

  async importItemLocalization(
    projectId: string,
    itemId: string,
    targetLocale: string,
    manifest: AdCreativeLocalizationManifest,
    operations: AdCreativeLocalizationOperation[],
    options: { dryRun?: boolean } = {},
  ): Promise<{ success: boolean; data?: { result: AdCreativeLocalizationApplyResult }; error?: string }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations/import`, {
      method: 'POST',
      body: { projectId, targetLocale, manifest, operations, dryRun: Boolean(options.dryRun) },
      projectId,
    });
  }

  async reviewItemLocalization(
    projectId: string,
    itemId: string,
    targetLocale: string,
    body: {
      approvalStage: 'pending' | 'approved' | 'rejected' | 'changes_requested';
      reviewerId?: string;
      notes?: string;
      requestedChanges?: string[];
      component?: 'text' | 'images' | 'video' | 'destination';
      /** Explicitly accepts an existing stale target against the current source hash. */
      reconcileStale?: boolean;
    },
  ): Promise<{
    success: boolean;
    data?: {
      item: AdCreativeItem;
      variant: AdCreativeLocalizationVariant;
      componentReview?: {
        id: string;
        component: 'text' | 'images' | 'video' | 'destination';
        approvalStage: 'pending' | 'approved' | 'rejected' | 'changes_requested';
        reconciledStale: boolean;
      };
    };
    error?: string;
  }> {
    return this.request(
      `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localizations/${encodeURIComponent(targetLocale)}/review`,
      {
        method: 'POST',
        body: { ...body, projectId },
        projectId,
      },
    );
  }

  async validateItem(
    projectId: string,
    itemId: string,
    locale: string,
    options: { fallbackApproved?: boolean } = {},
  ): Promise<{ success: boolean; data?: { validation: AdCreativeValidationRun }; error?: string }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/validate`, {
      method: 'POST',
      body: { projectId, locale, fallbackApproved: options.fallbackApproved === true },
      projectId,
    });
  }

  async listItemDestinations(
    projectId: string,
    itemId: string,
    locale: string,
    options: { fallbackApproved?: boolean; limit?: number } = {},
  ): Promise<{
    success: boolean;
    data?: { catalog: AdCreativeDestinationCatalogView };
    error?: string;
  }> {
    const params = new URLSearchParams({ projectId, locale });
    if (options.fallbackApproved) params.set('fallbackApproved', 'true');
    if (options.limit != null) params.set('limit', String(options.limit));
    return this.request(
      `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/destinations?${params.toString()}`,
      { method: 'GET', projectId },
    );
  }

  async generateItemVideo(
    projectId: string,
    itemId: string,
    body: {
      model?: string;
      targetDurationSec?: number;
      resolution?: '720p' | '1080p';
      regenerate?: 'plan' | 'keyframes' | 'videos' | 'all' | 'segment';
      /** Required with regenerate='segment' — re-renders only this scene. */
      segmentIndex?: number;
      /** Scene-card edits applied before regeneration; a keyframePrompt patch forces that scene's keyframe to regenerate (FR5). */
      segmentPatches?: Array<{ index: number; motionPrompt?: string; durationSec?: number; keyframePrompt?: string }>;
      /** With regenerate='plan': also render the scenes' first-frame keyframe images (cents, no Veo). */
      withKeyframes?: boolean;
      userInstruction?: string;
      generation?: { textModel?: string; reasoningEffort?: 'low' | 'medium' | 'high'; maxOutputTokens?: number };
    } = {},
  ): Promise<{ success: boolean; data?: { taskId: string; task: Record<string, unknown> }; error?: string }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/generate-video`, {
      method: 'POST',
      body: { ...body, projectId },
      projectId,
    });
  }

  /** FR2: request cancellation of the running video generation for an item. */
  async cancelItemVideo(
    projectId: string,
    itemId: string,
    body: { taskId?: string } = {},
  ): Promise<{ success: boolean; data?: { taskId: string; status: string; alreadyTerminal: boolean }; error?: string }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/generate-video/cancel`, {
      method: 'POST',
      body: { ...body, projectId },
      projectId,
    });
  }

  /**
   * FR5 storyboard plan ops. useTake restitches (free); insertScene may draft
   * prompts via a text agent (cents); regenerateKeyframe is an image call.
   */
  async videoPlanOps(
    projectId: string,
    itemId: string,
    op:
      | { op: 'useTake'; segmentIndex: number; takeId: string }
      | { op: 'insertScene'; position: number; role: string; durationSec: number; sourcePath?: string; draft?: { keyframePrompt: string; motionPrompt: string } }
      | { op: 'retime'; segmentIndex: number; durationSec: number }
      | { op: 'setSceneSource'; segmentIndex: number; sourcePath: string | null }
      | { op: 'setKeyframe'; segmentIndex: number; source: 'upload' | 'experiment' | 'assetUrl'; assetUrl?: string; experimentId?: string }
      | { op: 'deleteTake'; segmentIndex: number; takeId: string }
      | {
        op: 'updateBrief';
        concept?: string;
        styleLock?: string;
        negativePrompt?: string;
        targetDurationSec?: number;
        /** FR10-I3: whole-draft hydration — grounding rows + adapted beats. */
        insightMapping?: Array<{ role: string; sourcePath?: string; label?: string }>;
        beats?: Array<{ label?: string; text?: string; sourcePath?: string }>;
      }
      | { op: 'regenerateKeyframe'; segmentIndex: number; imageModel?: string },
  ): Promise<{
    success: boolean;
    data?: {
      videoPlan: Record<string, unknown>;
      restitch?: { videoUrl: string; posterUrl: string; durationSec: number };
      draftedPrompts?: { keyframePrompt: string; motionPrompt: string };
      videoBrief?: Record<string, unknown>;
      keyframe?: { url: string; experimentId: string };
    };
    error?: string;
  }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/video-plan/ops`, {
      method: 'POST',
      body: { ...op, projectId },
      projectId,
    });
  }

  async exportItem(
    projectId: string,
    itemId: string,
    body: {
      locale: string;
      renditionId?: string;
      localizationVariantId?: string;
      fallbackApproved?: boolean;
      destination?: Record<string, unknown>;
      utm?: Record<string, unknown>;
    },
  ): Promise<{ success: boolean; data?: { manifest: AdCreativeExportManifest }; error?: string }> {
    return this.request(`/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/export`, {
      method: 'POST',
      body: { ...body, projectId },
      projectId,
    });
  }

  async getPlatformSettings(projectId: string, networkId = 'meta'): Promise<PlatformSettingsResponse> {
    const params = new URLSearchParams({ projectId });
    return this.request(`/api/agent/ad-creative/platforms/${encodeURIComponent(networkId)}/settings?${params.toString()}`, {
      method: 'GET',
      projectId,
    });
  }

  async updatePlatformSettings(
    projectId: string,
    patch: Partial<AdCreativePlatformSettings>,
    networkId = 'meta',
  ): Promise<PlatformSettingsResponse> {
    return this.request(`/api/agent/ad-creative/platforms/${encodeURIComponent(networkId)}/settings`, {
      method: 'PATCH',
      body: { ...patch, projectId },
      projectId,
    });
  }

  /**
   * Batch launch (decision #3 primary path): creates ONE paused ad set (or
   * targets an existing one) and a paused ad per item. Builder-owned route —
   * same credential rules as per-item meta drafts.
   */
  async createBatchMetaLaunch(request: BatchMetaLaunchRequest): Promise<BatchMetaLaunchResponse> {
    return this.request(`/api/ad-creative/briefs/${encodeURIComponent(request.briefId)}/meta-launch`, {
      method: 'POST',
      body: request,
      projectId: request.projectId,
      baseUrl: this.builderBaseUrl,
    });
  }

  async createMetaDraft(
    projectId: string,
    itemId: string,
    body: {
      /** Exact authored locale exported to this provider draft. */
      locale: string;
      adAccountId: string;
      pageId: string;
      campaignId: string;
      adSetId: string;
      fallbackApproved?: boolean;
      utm?: Record<string, string>;
      name?: string;
    },
  ): Promise<CreateMetaDraftResponse> {
    return this.request(`/api/ad-creative/items/${encodeURIComponent(itemId)}/meta-draft`, {
      method: 'POST',
      body: { ...body, projectId },
      projectId,
      baseUrl: this.builderBaseUrl,
    });
  }

  async checkMetaDraftStatus(projectId: string, draftId: string): Promise<CheckMetaDraftStatusResponse> {
    return this.request(`/api/ad-creative/meta-drafts/${encodeURIComponent(draftId)}/check-status`, {
      method: 'POST',
      body: { projectId },
      projectId,
      baseUrl: this.builderBaseUrl,
    });
  }

  /**
   * E5 feedback join (U7): funnel-warehouse CAC rows already joined with Meta
   * delivery by utm_content/ad id. The UI matches rows to creatives via
   * metaAdId == item.providerAdId to show TRUE per-creative conversions.
   */
  async getCacByCampaign(
    projectId: string,
    options: { from?: string; to?: string } = {},
  ): Promise<{
    result?: { campaigns?: Array<Record<string, unknown>> };
    campaigns?: Array<Record<string, unknown>>;
    data?: { campaigns?: Array<Record<string, unknown>> };
    warnings?: Array<Record<string, unknown>>;
    success?: boolean;
    error?: string;
  }> {
    const params = new URLSearchParams();
    if (options.from) params.set('from', options.from);
    if (options.to) params.set('to', options.to);
    const query = params.toString();
    return this.request(`/api/projects/${encodeURIComponent(projectId)}/insights/v1/cac-by-campaign${query ? `?${query}` : ''}`, {
      method: 'GET',
      projectId,
      baseUrl: this.builderBaseUrl,
    });
  }

  /**
   * Transport core (FR0-I1): dedupe of identical concurrent calls, abortKey
   * supersede semantics, 30s timeout, token memoized until a 401 forces one
   * refreshed retry. Logic lives in AdCreativeRequestTransport (unit-tested;
   * this file can't be jest-loaded because of import.meta in the constructor).
   */
  private readonly transport = new AdCreativeRequestTransport({
    fetchToken: async (forceRefresh) => (await getAuth().currentUser?.getIdToken(forceRefresh)) ?? null,
    timeoutMs: REQUEST_TIMEOUT_MS,
    onHttpError: (status, data, request) => {
      const blocked = parseBillingBlockedPayload(status, data);
      if (!blocked) return undefined;
      emitBillingBlocked(blocked.billingBlock, {
        projectId: request.projectId,
        actionLabel: request.path,
      });
      return { success: false, error: blocked.message, billingBlock: blocked.billingBlock };
    },
    unreachableHint: (baseUrl) => (baseUrl.includes('dev-api.segmently.ai')
      ? ' Local development should use VITE_AGENT_API_URL=http://localhost:8080 and VITE_BUILDER_API_URL=http://localhost:8081, then restart Vite.'
      : ''),
  });

  private async request<T>(
    path: string,
    options: {
      method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
      body?: unknown;
      formData?: FormData;
      projectId?: string;
      baseUrl?: string;
      /** Requests sharing an abortKey supersede each other (newer aborts older). */
      abortKey?: string;
      /** Narrow per-call timeout override; generic calls retain the 30s default. */
      timeoutMs?: number;
    },
  ): Promise<T> {
    return this.transport.request<T>({
      path,
      method: options.method,
      body: options.body,
      formData: options.formData,
      baseUrl: options.baseUrl || this.baseUrl,
      abortKey: options.abortKey,
      projectId: options.projectId,
      timeoutMs: options.timeoutMs,
    });
  }
}
